import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../../services/loading.service';
import { ToastService } from '../../../services/toast.service';
import { EnrollmentService } from '../../../services/enrollment.service';
import { CorporateEnrollmentService, CompanyPaymentPlan, MasterPaymentPlan } from '../../../services/corporate-enrollment.service';
import { AuthorizationService } from '../../../services/authorization.service';
import { CorporatePortfolioMovementService, CorporatePortfolioMovementResponse } from '../../../services/corporate-portfolio-movement.service';
import { ExchangeRateService } from '../../../services/exchange-rate.service';
import { RefundVouchersService } from '../../../services/refund-vouchers.service';
import { ConfirmationConfig } from '../../../components/shared/confirmation-modal/confirmation-modal.component';
import { EmailLogService, EmailLogResponseDTO } from '../../../services/email-log.service';
import { AuthService } from '../../../services/auth.service';
import { CorporatePaymentPlanAdminService } from '../../../services/corporate-payment-plan-admin.service';
import { IncomeReceiptsService } from '../../../services/income-receipts.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  recaudoPercentage,
  nextDueInstallment,
  activeVsWithdrawnSummary,
  InstallmentLike,
} from './corporate-v2-kpis';

export type CorporateV2Tab = 'empresa' | 'participantes' | 'ingresos' | 'auditoria';
const VALID_CORPORATE_V2_TABS: CorporateV2Tab[] = ['empresa', 'participantes', 'ingresos', 'auditoria'];

@Component({
  selector: 'app-view-corporate-enrollment-v2',
  templateUrl: './view-corporate-enrollment-v2.component.html',
  styleUrls: ['./view-corporate-enrollment-v2.component.scss']
})
export class ViewCorporateEnrollmentV2Component implements OnInit {
  // ====== Estado v2: tabs y expanded participant ======
  activeTab: CorporateV2Tab = 'empresa';
  expandedParticipantId: number | null = null;

  // ====== Estado v2: filtros y orden de participantes ======
  participantSearch = '';
  participantStatusFilter: 'all' | 'active' | 'withdrawn' | 'postponed' = 'all';
  participantSort: 'balance-desc' | 'name-asc' | 'recaudo-desc' | 'next-due-asc' = 'balance-desc';

  // ====== Estado v2: filtros de auditoría ======
  auditTypeFilter = 'all';
  auditUserFilter = 'all';
  auditDateFrom = '';
  auditDateTo = '';

  agreementId!: number;
  details: any;
  today = new Date();

  // Offcanvas para planes de pago
  selectedParticipant: any = null;
  isOffcanvasOpen = false;

  // 🆕 Historial de movimientos de cartera corporativa
  movementHistory: CorporatePortfolioMovementResponse[] = [];

  // 🆕 Última cotización vigente para planes pendientes
  currentExchangeRate: number | null = null;

  // 🆕 Devoluones relacionadas con la inscripción corporativa
  refundVouchers: any[] = [];

  // 🆕 Ingresos generales (sin plan de pago) del acuerdo corporativo
  generalIncomeReceipts: any[] = [];

  // Tab state para planes de pago (empresa y participante)
  companyPlanTabs: { [planId: number]: string } = {};
  participantPlanTabs: { [planId: number]: string } = {};

  // Email logs por plan de pago
  emailsByPlan: { [planId: number]: EmailLogResponseDTO[] } = {};
  emailsLoadingByPlan: { [planId: number]: boolean } = {};

  // Opciones y valor seleccionado para el dropdown de estado
  enrollmentStatusOptions: Array<{ value: string; label: string }> = [
    { value: 'Pending', label: 'Pendiente' },
    { value: 'Active', label: 'Activo' },
    { value: 'Withdrawn', label: 'Retirado' },
    { value: 'Deferred', label: 'Aplazado' },
    { value: 'Completed', label: 'Completado' },
    { value: 'Refinanced', label: 'Refinanciado' },
    { value: 'Reversed', label: 'Reversado' },
    { value: 'Refund', label: 'Reintegro' },
    { value: 'SlotTransfer', label: 'Cesión de Cupo' }
  ];
  selectedEnrollmentStatus: string = 'Active'; // Por defecto Activo

  // Modal de confirmación para cambio de estado
  showStatusConfirmationModal: boolean = false;
  statusConfirmationConfig: ConfirmationConfig = {
    title: 'Confirmar cambio de estado',
    message: '',
    type: 'warning',
    confirmText: 'Sí, cambiar estado',
    cancelText: 'Cancelar'
  };
  pendingStatusChange: { newStatus: string; statusLabel: string } | null = null;
  showReasonInput: boolean = false;
  statusChangeReason: string = '';

  // 🆕 Modal de anulación
  showCancellationModal: boolean = false;
  cancellationReason: string = '';
  isCancelling: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private loading: LoadingService,
    private toast: ToastService,
    private enrollmentService: EnrollmentService,
    private corporateService: CorporateEnrollmentService,
    private corporatePortfolioService: CorporatePortfolioMovementService,
    private router: Router,
    private authorizationService: AuthorizationService,
    private exchangeRateService: ExchangeRateService,
    private refundVouchersService: RefundVouchersService,
    public emailLogService: EmailLogService,
    private authService: AuthService,
    private adminService: CorporatePaymentPlanAdminService,
    private incomeReceiptsService: IncomeReceiptsService
  ) { }

  // 🆕 ====== Estado del modal de anulación de comprobante ======
  cancelReceiptModal: {
    open: boolean;
    receipt: any | null;
    reason: string;
    submitting: boolean;
  } = { open: false, receipt: null, reason: '', submitting: false };

  /**
   * Indica si un comprobante puede anularse desde la lista de comprobantes activos.
   * Mismo criterio que la vista individual. El backend valida adicionalmente
   * el permiso `INCOME_CANCEL`.
   */
  canCancelReceipt(receipt: any): boolean {
    if (!receipt) return false;
    if (receipt.isCancelled) return false;
    if (receipt.isBankReconciled === true) return false;
    if (receipt.approvalStatus === 'PendingApproval') return false;
    return true;
  }

  openCancelReceiptModal(receipt: any): void {
    if (!this.canCancelReceipt(receipt)) return;
    this.cancelReceiptModal = {
      open: true,
      receipt,
      reason: '',
      submitting: false
    };
  }

  closeCancelReceiptModal(): void {
    if (this.cancelReceiptModal.submitting) return;
    this.cancelReceiptModal = { open: false, receipt: null, reason: '', submitting: false };
  }

  confirmCancelReceipt(): void {
    const receipt = this.cancelReceiptModal.receipt;
    if (!receipt || this.cancelReceiptModal.submitting) return;

    const reason = (this.cancelReceiptModal.reason || '').trim();
    if (!reason) {
      this.toast.showToast('error', 'Debe especificar el motivo de anulación');
      return;
    }
    if (reason.length < 10) {
      this.toast.showToast('error', 'El motivo debe tener al menos 10 caracteres');
      return;
    }

    this.cancelReceiptModal.submitting = true;
    this.loading.show();

    this.incomeReceiptsService.cancelIncomeReceipt(receipt.id, reason).subscribe({
      next: () => {
        this.toast.showToast('success', '✅ Comprobante anulado exitosamente');
        this.cancelReceiptModal = { open: false, receipt: null, reason: '', submitting: false };
        // Recarga los detalles del acuerdo para reflejar PaymentStatus y receipt anulado.
        this.loadDetails();
      },
      error: (error: any) => {
        console.error('Error al anular comprobante:', error);
        const errorMessage = error?.error?.errorMessage
          || (typeof error?.error === 'string' ? error.error : null)
          || 'Error al anular el comprobante';
        this.toast.showToast('error', errorMessage);
        this.cancelReceiptModal.submitting = false;
      },
      complete: () => {
        this.loading.hide();
      }
    });
  }

  /**
   * 🆕 Abre los comprobantes de ingreso EMPRESARIALES (acuerdo a nivel
   * empresa) en una nueva pestaña, filtrados por NIT/razón social + projectId.
   * Mismo patrón que el dashboard de auditoría.
   */
  goToReceiptsCompany(): void {
    if (!this.details) {
      this.toast.showToast('error', 'El acuerdo aún no se cargó.');
      return;
    }
    const search = this.details?.company?.nit
                ?? this.details?.company?.identification
                ?? this.details?.company?.businessName
                ?? this.details?.company?.name
                ?? String(this.agreementId);
    const projectId = this.details?.project?.id ?? null;
    const queryParams: any = { search: String(search) };
    if (projectId) queryParams.projectId = projectId;
    const tree = this.router.createUrlTree(
      ['/dashboard/income-receipts/business'],
      { queryParams }
    );
    window.open(this.router.serializeUrl(tree), '_blank');
  }

  /**
   * 🆕 Abre los comprobantes de ingreso INDIVIDUALES del participante actual
   * (cuando el offcanvas de un participante está abierto), filtrados por
   * documento + projectId. Se usa el módulo individual porque los pagos del
   * participante se registran como individuales aún en acuerdos corporativos.
   */
  goToReceiptsParticipant(): void {
    const p: any = (this as any).selectedParticipant?.enrollment?.participant;
    const projectId = this.details?.project?.id ?? null;
    if (!p) {
      this.toast.showToast('error', 'No se pudo identificar el participante.');
      return;
    }
    const documentId = p.documentId ?? p.documentNumber ?? '';
    const queryParams: any = { search: documentId || String(p.id) };
    if (projectId) queryParams.projectId = projectId;
    const tree = this.router.createUrlTree(
      ['/dashboard/income-receipts/individual'],
      { queryParams }
    );
    window.open(this.router.serializeUrl(tree), '_blank');
  }

  // ============================================================
  // 🔐 Acciones administrativas sobre cuotas corporativas
  // (espejo de view-enrollment-v2; gated por permiso ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS)
  // ============================================================
  get isSuperAdmin(): boolean {
    return this.authorizationService.hasPermission('ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS');
  }

  adminModal: {
    open: boolean;
    mode: 'menu' | 'renumber' | 'date' | 'delete' | 'status' | 'attach' | 'recalculate' | 'punish-remaining' | null;
    plan: any | null;
    newNumber: number | null;
    newDate: string;
    newStatus: 'PENDING' | 'PAID' | 'OVERDUE' | 'PARTIALLY_PAID' | 'LINK_GENERATED' | 'AUTO';
    comment: string;
    forceIfHasReceipts: boolean;
    attachReceiptId: number | null;
    forceFromOtherEnrollment: boolean;
  } = {
    open: false, mode: null, plan: null,
    newNumber: null, newDate: '', newStatus: 'AUTO',
    comment: '', forceIfHasReceipts: false,
    attachReceiptId: null, forceFromOtherEnrollment: false
  };

  private resetAdminModal(plan: any): void {
    this.adminModal = {
      open: true, mode: null, plan,
      newNumber: null, newDate: '', newStatus: 'AUTO',
      comment: '', forceIfHasReceipts: false,
      attachReceiptId: null, forceFromOtherEnrollment: false
    };
  }

  openAdminMenu(plan: any): void {
    this.resetAdminModal(plan);
    this.adminModal.mode = 'menu';
  }

  selectAdminMode(mode: 'renumber' | 'date' | 'delete' | 'status' | 'attach' | 'recalculate' | 'punish-remaining'): void {
    const plan = this.adminModal.plan;
    if (!plan) return;
    const keepComment = this.adminModal.comment;
    this.adminModal.mode = mode;
    this.adminModal.newNumber = mode === 'renumber' ? plan.installmentNumber : null;
    this.adminModal.newStatus = 'AUTO';
    this.adminModal.attachReceiptId = null;
    this.adminModal.forceFromOtherEnrollment = false;
    this.adminModal.forceIfHasReceipts = false;
    this.adminModal.comment = keepComment;
    if (mode === 'date') {
      const d = plan.dueDate ? new Date(plan.dueDate) : new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      this.adminModal.newDate = `${yyyy}-${mm}-${dd}`;
    } else {
      this.adminModal.newDate = '';
    }
  }

  backToAdminMenu(): void {
    this.adminModal.mode = 'menu';
  }

  closeAdminModal(): void {
    this.adminModal.open = false;
  }

  /**
   * 🆕 Drag & drop sobre cuotas de EMPRESA del acuerdo corporativo.
   * Reordena el array `details.companyPaymentPlans` y persiste.
   */
  onCompanyPlanDrop(event: CdkDragDrop<any[]>): void {
    if (!this.isSuperAdmin) return;
    if (event.previousIndex === event.currentIndex) return;
    const plans = this.details?.companyPaymentPlans;
    if (!plans?.length || !this.agreementId) return;
    moveItemInArray(plans, event.previousIndex, event.currentIndex);
    plans.forEach((p: any, idx: number) => p.installmentNumber = idx + 1);
    this.applyReorder(plans.map((p: any) => p.id));
  }

  /**
   * 🆕 Drag & drop sobre cuotas del PARTICIPANTE seleccionado en el offcanvas.
   * Reordena `selectedParticipant.paymentPlans` y persiste.
   */
  onParticipantPlanDrop(event: CdkDragDrop<any[]>): void {
    if (!this.isSuperAdmin) return;
    if (event.previousIndex === event.currentIndex) return;
    const plans = this.selectedParticipant?.paymentPlans;
    if (!plans?.length || !this.agreementId) return;
    moveItemInArray(plans, event.previousIndex, event.currentIndex);
    plans.forEach((p: any, idx: number) => p.installmentNumber = idx + 1);
    this.applyReorder(plans.map((p: any) => p.id));
  }

  private applyReorder(orderedIds: number[]): void {
    this.loading.show();
    this.adminService.reorder(this.agreementId, orderedIds).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        this.toast.showToast('success', data?.message ?? 'Cuotas reordenadas.');
        this.loadDetails();
      },
      error: (err) => {
        const msg = err?.error?.message ?? err?.error ?? 'No se pudo reordenar.';
        this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error.');
        this.loadDetails();
      },
      complete: () => this.loading.hide()
    });
  }

  confirmAdminAction(): void {
    if (!this.adminModal.plan || !this.adminModal.mode || this.adminModal.mode === 'menu') return;
    const c = (this.adminModal.comment || '').trim();
    if (this.adminModal.mode !== 'recalculate' && c.length < 10) {
      this.toast.showToast('error', 'Comentario obligatorio (mín. 10 caracteres) para audit trail.');
      return;
    }

    const planId = this.adminModal.plan.id;
    this.loading.show();

    let req$;
    if (this.adminModal.mode === 'renumber') {
      if (!this.adminModal.newNumber || this.adminModal.newNumber <= 0) {
        this.loading.hide();
        this.toast.showToast('error', 'Número de cuota debe ser > 0.');
        return;
      }
      req$ = this.adminService.renumber(planId, {
        newInstallmentNumber: this.adminModal.newNumber, comment: c
      });
    } else if (this.adminModal.mode === 'date') {
      if (!this.adminModal.newDate) {
        this.loading.hide();
        this.toast.showToast('error', 'Fecha requerida.');
        return;
      }
      req$ = this.adminService.changeDueDate(planId, {
        newDueDate: new Date(this.adminModal.newDate).toISOString(), comment: c
      });
    } else if (this.adminModal.mode === 'status') {
      req$ = this.adminService.changeStatus(planId, {
        newStatus: this.adminModal.newStatus, comment: c
      });
    } else if (this.adminModal.mode === 'recalculate') {
      req$ = this.adminService.recalculateStatus(planId);
    } else if (this.adminModal.mode === 'punish-remaining') {
      req$ = this.adminService.punishRemaining(planId, { comment: c });
    } else if (this.adminModal.mode === 'attach') {
      if (!this.adminModal.attachReceiptId || this.adminModal.attachReceiptId <= 0) {
        this.loading.hide();
        this.toast.showToast('error', 'ID del comprobante requerido.');
        return;
      }
      req$ = this.adminService.attachReceipt(planId, {
        receiptId: this.adminModal.attachReceiptId, comment: c,
        forceFromOtherEnrollment: this.adminModal.forceFromOtherEnrollment
      });
    } else {
      req$ = this.adminService.delete(planId, {
        comment: c, forceIfHasReceipts: this.adminModal.forceIfHasReceipts
      });
    }

    req$.subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        this.toast.showToast('success', data?.message ?? 'Operación aplicada.');
        this.closeAdminModal();
        this.loadDetails();
      },
      error: (err) => {
        const msg = err?.error?.message ?? err?.error ?? 'Error aplicando operación.';
        this.toast.showToast('error', typeof msg === 'string' ? msg : 'Error.');
      },
      complete: () => this.loading.hide()
    });
  }

  // === Permisos de gestión de cartera corporativa ===
  get canWithdrawCorporateEnrollments(): boolean {
    return this.authorizationService.hasPermission('CORPORATE_ENROLLMENTS_WITHDRAW');
  }

  get canReverseCorporateWithdrawals(): boolean {
    return this.authorizationService.hasPermission('CORPORATE_ENROLLMENTS_REVERSAL');
  }

  get canDeferCorporateEnrollments(): boolean {
    return this.authorizationService.hasPermission('CORPORATE_ENROLLMENTS_DEFER');
  }

  // === Navegación a wizards de cartera corporativa ===
  goToWithdrawal(): void {
    if (!this.agreementId) return;
    this.router.navigate(['/dashboard/enrollments/corporate-withdrawal-wizard'], {
      queryParams: { corporateEnrollmentId: this.agreementId }
    });
  }

  goToReversal(): void {
    if (!this.agreementId) return;
    this.router.navigate(['/dashboard/enrollments/corporate-reversal-wizard'], {
      queryParams: { corporateEnrollmentId: this.agreementId }
    });
  }

  goToDeferral(): void {
    if (!this.agreementId) return;
    this.router.navigate(['/dashboard/enrollments/corporate-deferral-wizard'], {
      queryParams: { corporateEnrollmentId: this.agreementId }
    });
  }

  // === Estado del acuerdo corporativo ===
  get enrollmentStatus(): string {
    return this.details?.status || 'Active';
  }

  get enrollmentStatusLabel(): string {
    if (this.isEnrollmentCancelled) return 'Anulado';
    switch (this.enrollmentStatus) {
      case 'Pending': return 'Pendiente';
      case 'Active': return 'Activo';
      case 'Withdrawn': return 'Retirado';
      case 'Deferred': return 'Aplazado';
      case 'Completed': return 'Completado';
      case 'Refinanced': return 'Refinanciado';
      case 'Reversed': return 'Reversado';
      case 'Refund': return 'Reintegro';
      case 'SlotTransfer': return 'Cesión de Cupo';
      default: return this.enrollmentStatus;
    }
  }

  getEnrollmentStatusClass(status: string): string {
    if (this.isEnrollmentCancelled) return 'bg-red-100 text-red-800';
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Withdrawn': return 'bg-red-100 text-red-800';
      case 'Deferred': return 'bg-orange-100 text-orange-800';
      case 'Completed': return 'bg-blue-100 text-blue-800';
      case 'Refinanced': return 'bg-indigo-100 text-indigo-800';
      case 'Reversed': return 'bg-gray-100 text-gray-800';
      case 'Refund': return 'bg-purple-100 text-purple-800';
      case 'SlotTransfer': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  get isEnrollmentActive(): boolean {
    return this.enrollmentStatus === 'Active';
  }

  get isEnrollmentWithdrawn(): boolean {
    return this.enrollmentStatus === 'Withdrawn';
  }

  get isEnrollmentDeferred(): boolean {
    return this.enrollmentStatus === 'Deferred';
  }

  get isEnrollmentCancelled(): boolean {
    return this.details?.isCancelled === true || this.enrollmentStatus === 'Reversed';
  }

  ngOnInit(): void {
    this.agreementId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadDetails();
    this.loadMovementHistory();
    this.loadRefundVouchers();
    this.loadGeneralIncomeReceipts();

    // v2: tab activo desde queryParam ?tab=
    this.route.queryParamMap.subscribe((q) => {
      const t = q.get('tab') as CorporateV2Tab | null;
      this.activeTab = t && VALID_CORPORATE_V2_TABS.includes(t) ? t : 'empresa';
    });
  }

  // ====== v2: navegación de tabs y expanded participant ======
  setTab(tab: CorporateV2Tab): void {
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
    });
  }

  toggleParticipant(participantId: number | undefined | null): void {
    if (participantId == null) return;
    this.expandedParticipantId = this.expandedParticipantId === participantId ? null : participantId;
  }

  trackParticipantById = (_: number, pe: any) => this.participantTrackKey(pe);

  // ====== v2: helpers KPI agregados ======
  // En la respuesta real del backend:
  //   - details.companyPaymentPlans[] es un array de CUOTAS (no de planes con cuotas anidadas).
  //   - details.participantEnrollments[].paymentPlans[] es también array de CUOTAS por participante.
  //   - Cada cuota expone: id, dueDate, amountToPay, amountPaid, paymentStatus, installmentNumber, isPunished.
  // Nombre del modelo conservado por compat con v1.
  private companyInstallmentsRaw(): any[] {
    return this.details?.companyPaymentPlans ?? [];
  }

  private participantInstallmentsRaw(): any[] {
    const out: any[] = [];
    for (const pe of this.details?.participantEnrollments ?? []) {
      for (const cuota of pe?.paymentPlans ?? []) out.push(cuota);
    }
    return out;
  }

  private toInstallmentLike(cuota: any): InstallmentLike {
    return {
      dueDate: cuota?.dueDate,
      paymentStatus: cuota?.paymentStatus,
      installmentNumber: cuota?.installmentNumber,
    };
  }

  private sumField(items: any[], field: string): number {
    return (items ?? []).reduce((acc: number, i: any) => acc + (Number(i?.[field]) || 0), 0);
  }

  // KPIs Empresa
  get companyTotalAmount(): number {
    return this.sumField(this.companyInstallmentsRaw(), 'amountToPay');
  }
  get companyTotalPaid(): number {
    return this.sumField(this.companyInstallmentsRaw(), 'amountPaid');
  }
  get companyPendingBalance(): number {
    return Math.max(0, this.companyTotalAmount - this.companyTotalPaid);
  }
  get companyOverdueAmount(): number {
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return this.companyInstallmentsRaw().reduce((acc: number, i: any) => {
      if ((i?.paymentStatus ?? '').toUpperCase() === 'PAID' || i?.isPunished) return acc;
      const due = new Date(i?.dueDate);
      if (Number.isNaN(due.getTime()) || due >= todayStart) return acc;
      const pending = Number(i?.amountToPay ?? 0) - Number(i?.amountPaid ?? 0);
      return acc + Math.max(0, pending);
    }, 0);
  }
  get companyRecaudoPercentage(): number {
    return recaudoPercentage(this.companyTotalPaid, this.companyTotalAmount);
  }
  get companyNextDue(): InstallmentLike | null {
    return nextDueInstallment(this.companyInstallmentsRaw().map((c: any) => this.toInstallmentLike(c)));
  }
  get companyInstallmentsCount(): number {
    return this.companyInstallmentsRaw().length;
  }

  // KPIs Participantes
  get participantsSummary() {
    const list = this.details?.participantEnrollments ?? [];
    // Per-participant withdrawal/aplazamiento status no se expone en el dto actual;
    // se asume activo salvo que enrollment lo marque explícitamente.
    return activeVsWithdrawnSummary(
      list.map((pe: any) => ({ status: pe?.enrollment?.status ?? pe?.status ?? 'ACTIVE' })),
    );
  }
  get participantsTotalAmount(): number {
    return this.sumField(this.participantInstallmentsRaw(), 'amountToPay');
  }
  get participantsTotalPaid(): number {
    return this.sumField(this.participantInstallmentsRaw(), 'amountPaid');
  }
  get participantsPendingBalance(): number {
    return Math.max(0, this.participantsTotalAmount - this.participantsTotalPaid);
  }
  get participantsRecaudoPercentage(): number {
    return recaudoPercentage(this.participantsTotalPaid, this.participantsTotalAmount);
  }
  get participantsPaidInstallmentsCount(): number {
    return this.participantInstallmentsRaw().filter(
      (i: any) => (i?.paymentStatus ?? '').toUpperCase() === 'PAID',
    ).length;
  }
  get participantsTotalInstallmentsCount(): number {
    return this.participantInstallmentsRaw().length;
  }
  get participantsNextDue(): InstallmentLike | null {
    return nextDueInstallment(this.participantInstallmentsRaw().map((c: any) => this.toInstallmentLike(c)));
  }

  // Helpers por participante individual (recibe pe = participantEnrollment)
  recaudoForParticipant(pe: any): number {
    return recaudoPercentage(this.participantTotalPaid(pe), this.participantTotalAmount(pe));
  }
  participantTotalAmount(pe: any): number {
    return this.sumField(pe?.paymentPlans ?? [], 'amountToPay');
  }
  participantTotalPaid(pe: any): number {
    return this.sumField(pe?.paymentPlans ?? [], 'amountPaid');
  }
  participantPendingBalance(pe: any): number {
    return Math.max(0, this.participantTotalAmount(pe) - this.participantTotalPaid(pe));
  }
  /** Matrícula ancla del participante; fallback a suma de cuotas. */
  getParticipantExpectedTotal(pe: any): number {
    const anchor = Number(pe?.expectedTotal ?? 0);
    return anchor > 0 ? anchor : this.participantTotalAmount(pe);
  }
  /** Faltante del participante por reasignar = ancla − suma de cuotas (>= 0). */
  getParticipantUnassignedAmount(pe: any): number {
    return Math.max(0, this.roundTwo(this.getParticipantExpectedTotal(pe) - this.participantTotalAmount(pe)));
  }
  /** Pendiente real del participante = ancla − pagado. */
  getParticipantExpectedPending(pe: any): number {
    return Math.max(0, this.roundTwo(this.getParticipantExpectedTotal(pe) - this.participantTotalPaid(pe)));
  }
  nextDueForParticipant(pe: any): InstallmentLike | null {
    return nextDueInstallment((pe?.paymentPlans ?? []).map((c: any) => this.toInstallmentLike(c)));
  }
  overdueForParticipant(pe: any): number {
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return (pe?.paymentPlans ?? []).reduce((acc: number, i: any) => {
      if ((i?.paymentStatus ?? '').toUpperCase() === 'PAID' || i?.isPunished) return acc;
      const due = new Date(i?.dueDate);
      if (Number.isNaN(due.getTime()) || due >= todayStart) return acc;
      const pending = Number(i?.amountToPay ?? 0) - Number(i?.amountPaid ?? 0);
      return acc + Math.max(0, pending);
    }, 0);
  }
  punishedForParticipant(pe: any): number {
    return (pe?.paymentPlans ?? []).reduce((acc: number, i: any) => {
      if (!i?.isPunished) return acc;
      const pending = Number(i?.amountToPay ?? 0) - Number(i?.amountPaid ?? 0);
      return acc + Math.max(0, pending);
    }, 0);
  }
  participantFullName(pe: any): string {
    const p = pe?.enrollment?.participant ?? pe?.participant;
    return `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim();
  }
  participantDocument(pe: any): string {
    const p = pe?.enrollment?.participant ?? pe?.participant;
    const type = p?.typeDocument ? `${p.typeDocument} ` : '';
    return `${type}${p?.documentId ?? ''}`.trim();
  }
  participantEmail(pe: any): string {
    const p = pe?.enrollment?.participant ?? pe?.participant;
    return p?.email ?? '';
  }
  participantId(pe: any): number | undefined {
    return pe?.enrollment?.participant?.id ?? pe?.participant?.id ?? pe?.id;
  }
  participantTrackKey(pe: any): any {
    return this.participantId(pe) ?? pe?.enrollment?.id ?? pe;
  }
  participantStatusBadgeClasses(status: string): string {
    const s = (status ?? '').toUpperCase();
    if (s === 'WITHDRAWN' || s === 'RETIRADA' || s === 'RETIRADO') return 'bg-gray-200 text-gray-700';
    if (s === 'POSTPONED' || s === 'APLAZADA' || s === 'APLAZADO' || s === 'DEFERRED') return 'bg-amber-100 text-amber-800';
    if (s === 'CANCELLED' || s === 'REVERSED' || s === 'ANULADO') return 'bg-red-100 text-red-800';
    return 'bg-emerald-100 text-emerald-800';
  }

  // ====== v2: filtrado/orden de participantes ======
  get filteredParticipants(): any[] {
    const search = (this.participantSearch ?? '').trim().toLowerCase();
    const list = (this.details?.participantEnrollments ?? []).filter((pe: any) => {
      if (search) {
        const haystack = `${this.participantFullName(pe)} ${this.participantDocument(pe)} ${this.participantEmail(pe)}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      const s = (pe?.enrollment?.status ?? pe?.status ?? '').toString().toUpperCase();
      const isWithdrawn = s === 'WITHDRAWN' || s === 'RETIRADA' || s === 'RETIRADO';
      const isPostponed = s === 'POSTPONED' || s === 'APLAZADA' || s === 'APLAZADO' || s === 'DEFERRED';
      if (this.participantStatusFilter === 'active' && (isWithdrawn || isPostponed)) return false;
      if (this.participantStatusFilter === 'withdrawn' && !isWithdrawn) return false;
      if (this.participantStatusFilter === 'postponed' && !isPostponed) return false;
      return true;
    });

    return [...list].sort((a: any, b: any) => {
      switch (this.participantSort) {
        case 'balance-desc':
          return this.participantPendingBalance(b) - this.participantPendingBalance(a);
        case 'name-asc':
          return this.participantFullName(a).localeCompare(this.participantFullName(b));
        case 'recaudo-desc':
          return this.recaudoForParticipant(b) - this.recaudoForParticipant(a);
        case 'next-due-asc': {
          const an = this.nextDueForParticipant(a);
          const bn = this.nextDueForParticipant(b);
          const ad = an ? new Date(an.dueDate).getTime() : Number.POSITIVE_INFINITY;
          const bd = bn ? new Date(bn.dueDate).getTime() : Number.POSITIVE_INFINITY;
          return ad - bd;
        }
      }
    });
  }

  // ====== v2: resumen de ingresos generales ======
  get generalIncomesTotalAmount(): number {
    return (this.generalIncomeReceipts ?? []).reduce((acc: number, g: any) => acc + (Number(g?.totalAmount) || 0), 0);
  }
  get generalIncomesNetAmount(): number {
    return (this.generalIncomeReceipts ?? []).reduce(
      (acc: number, g: any) => acc + (Number(g?.netAmount ?? g?.totalAmount) || 0),
      0,
    );
  }
  get generalIncomesLastDate(): Date | null {
    const dates = (this.generalIncomeReceipts ?? [])
      .map((g: any) => new Date(g?.createdAt ?? g?.issuedAt ?? g?.receiptDate))
      .filter((d: Date) => !Number.isNaN(d.getTime()));
    if (dates.length === 0) return null;
    return new Date(Math.max(...dates.map((d: Date) => d.getTime())));
  }

  // ====== v2: helpers para banners ======
  get isWithdrawnOrPostponed(): boolean {
    const s = (this.details?.status ?? this.enrollmentStatus ?? '').toString().toUpperCase();
    return s === 'WITHDRAWN' || s === 'DEFERRED' || s === 'RETIRADA' || s === 'RETIRADO' || s === 'APLAZADA' || s === 'APLAZADO';
  }
  private punishedPendingAmount(cuotas: any[]): number {
    return (cuotas ?? []).reduce((acc: number, i: any) => {
      if (!i?.isPunished) return acc;
      const pending = Number(i?.amountToPay ?? 0) - Number(i?.amountPaid ?? 0);
      return acc + Math.max(0, pending);
    }, 0);
  }
  get hasGlobalPunishedPortfolio(): boolean {
    return this.globalPunishedAmount > 0;
  }
  get globalPunishedAmount(): number {
    return (
      this.punishedPendingAmount(this.companyInstallmentsRaw()) +
      this.punishedPendingAmount(this.participantInstallmentsRaw())
    );
  }
  recalcCompanySums(): void {
    this.loadDetails();
  }
  get latestPortfolioMovementReason(): string | null {
    const m = (this.movementHistory ?? [])[0];
    return m?.reason ?? null;
  }
  statusBadgeClasses(status: string): string {
    return this.participantStatusBadgeClasses(status);
  }
  get canChangeStatus(): boolean {
    return !!this.authorizationService?.hasPermission?.('ENROLLMENTS_UPDATE');
  }
  get availableStatuses(): Array<{ value: string; label: string }> {
    return this.enrollmentStatusOptions;
  }
  get selectedStatus(): string {
    return this.selectedEnrollmentStatus;
  }
  set selectedStatus(v: string) {
    this.selectedEnrollmentStatus = v;
  }
  get canEditObservations(): boolean {
    return !!this.authorizationService?.hasPermission?.('ENROLLMENTS_UPDATE');
  }
  get canAddGeneralIncome(): boolean {
    return !!this.authorizationService?.hasPermission?.('INCOME_CREATE');
  }
  openParticipantActions(p: any): void {
    this.toggleParticipant(p?.id);
  }
  openNewGeneralIncome(): void {
    this.router.navigate(['/dashboard/income-receipts/new'], {
      queryParams: { corporateAgreementId: this.agreementId },
    });
  }
  // Estado para AGREGAR nueva observación (append, no edit)
  isAddingObservation = false;
  newObservationDraft = '';
  isSavingObservation = false;
  startAddingObservation(): void {
    this.newObservationDraft = '';
    this.isAddingObservation = true;
  }
  cancelAddingObservation(): void {
    this.isAddingObservation = false;
    this.newObservationDraft = '';
  }
  saveNewObservation(): void {
    const note = (this.newObservationDraft || '').trim();
    if (note.length < 3) {
      this.toast.showToast('warning', 'La observación debe tener al menos 3 caracteres.');
      return;
    }
    if (!this.agreementId) return;
    this.isSavingObservation = true;
    this.loading.show();
    this.corporateService.appendObservation(this.agreementId, note).subscribe({
      next: () => {
        this.toast.showToast('success', 'Observación agregada.');
        this.isAddingObservation = false;
        this.newObservationDraft = '';
        this.isSavingObservation = false;
        this.loadDetails();
      },
      error: (err) => {
        this.isSavingObservation = false;
        const msg = err?.error?.message ?? err?.error ?? 'No se pudo agregar la observación.';
        this.toast.showToast('error', msg);
        this.loading.hide();
      }
    });
  }

  // Aliases legacy (compat templates anteriores)
  isEditingAgreementObservations = false;
  agreementObservationsDraft = '';
  startEditingAgreementObservations(): void { this.startAddingObservation(); }
  cancelEditingAgreementObservations(): void { this.cancelAddingObservation(); }
  saveAgreementObservations(): void { this.saveNewObservation(); }
  // Audit events alias para template (v1 los maneja vía subcomponente; v2 expone getter neutro)
  get auditEvents(): any[] {
    return (this.details?.auditEvents ?? []) as any[];
  }
  get availableAuditTypes(): string[] {
    return Array.from(new Set((this.auditEvents ?? []).map((e: any) => e?.type).filter(Boolean))).sort();
  }
  get availableAuditUsers(): Array<{ id: any; name: string }> {
    const map = new Map<any, string>();
    for (const e of this.auditEvents ?? []) {
      if (e?.userId && !map.has(e.userId)) map.set(e.userId, e.userName ?? 'Usuario');
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }
  get filteredAuditEvents(): any[] {
    const from = this.auditDateFrom ? new Date(this.auditDateFrom).getTime() : -Infinity;
    const to = this.auditDateTo ? new Date(this.auditDateTo).getTime() + 24 * 60 * 60 * 1000 : Infinity;
    return (this.auditEvents ?? []).filter((e: any) => {
      if (this.auditTypeFilter !== 'all' && e?.type !== this.auditTypeFilter) return false;
      if (this.auditUserFilter !== 'all' && e?.userId !== this.auditUserFilter) return false;
      const t = new Date(e?.createdAt).getTime();
      if (Number.isNaN(t) || t < from || t > to) return false;
      return true;
    });
  }
  get corporatePortfolioMovements(): CorporatePortfolioMovementResponse[] {
    return this.movementHistory ?? [];
  }


  /**
   * 🆕 Carga los ingresos generales (sin plan de pago) del acuerdo corporativo.
   * Incluye pagos de la empresa y de los participantes que no están vinculados a un plan.
   */
  loadGeneralIncomeReceipts(): void {
    if (!this.agreementId) { return; }
    this.enrollmentService.getCorporateGeneralIncomeReceipts(this.agreementId).subscribe({
      next: (response) => {
        this.generalIncomeReceipts = this.extractIncomeReceiptsData(response);
      },
      error: (err) => {
        console.error('Error al cargar ingresos generales corporativos:', err);
        this.generalIncomeReceipts = [];
      }
    });
  }

  private extractIncomeReceiptsData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  getTotalGeneralIncome(): number {
    if (!this.generalIncomeReceipts || this.generalIncomeReceipts.length === 0) {
      return 0;
    }
    return this.generalIncomeReceipts.reduce((sum, r) => sum + (r.netAmount || 0), 0);
  }

  /**
   * Devuelve una etiqueta legible del pagador de un ingreso general.
   * Usa el `payerDisplayName` que ya viene calculado del backend, con fallback.
   */
  getReceiptPayerLabel(receipt: any): string {
    if (receipt?.payerDisplayName) return receipt.payerDisplayName;
    if (receipt?.company && !receipt?.participant) {
      return receipt.company.name || 'Empresa';
    }
    if (receipt?.participant) {
      const p = receipt.participant;
      return [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || 'Participante';
    }
    return 'Otro pagador';
  }

  /**
   * Devuelve el tipo de pagador (Corporativo, Individual, Participante Corporativo).
   */
  getReceiptPayerType(receipt: any): string {
    return receipt?.payerTypeName || '';
  }

  // === REFINANCIACIÓN ===
  refinanceCompany(): void {
    // Obsoleto: navegación a pantalla dedicada
    this.goToEditCompanyPlans();
  }

  // === NAVEGACIÓN A EDICIÓN ===
  // 🆕 Spinners del recálculo masivo previo a editar plan (por scope).
  isRecalculatingCompany = false;
  recalculatingParticipantId: number | null = null;

  goToEditCompanyPlans(): void {
    if (!this.details?.agreementId || this.isRecalculatingCompany) return;
    const id = this.details.agreementId;
    const viewUrl = `/dashboard/enrollments/corporate/view-v2/${id}`;
    const url = this.router.serializeUrl(this.router.createUrlTree(
      ['/dashboard/enrollments/corporate/edit-company', id], { queryParams: { returnTo: viewUrl } }));
    const win = window.open('', '_blank');
    const open = () => { if (win) win.location.href = url; else this.router.navigateByUrl(url); };
    if (!this.isSuperAdmin) { open(); return; }
    this.isRecalculatingCompany = true;
    this.adminService.recalculateAll(id, 'company').subscribe({
      next: (resp: any) => {
        const changed = Number((resp?.data ?? resp)?.totalChanged ?? 0);
        if (changed > 0) this.toast.showToast('info', `Se actualizaron ${changed} cuota(s) de empresa.`);
        this.isRecalculatingCompany = false; open();
      },
      error: () => {
        this.isRecalculatingCompany = false;
        this.toast.showToast('warning', 'No se pudieron recalcular los pagos; abriendo el editor igualmente.'); open();
      }
    });
  }

  goToEditParticipantPlans(participant: any): void {
    const participantId = participant?.enrollment?.participant?.id;
    if (!this.details?.agreementId || !participantId || this.recalculatingParticipantId) return;
    const id = this.details.agreementId;
    const viewUrl = `/dashboard/enrollments/corporate/view-v2/${id}`;
    const url = this.router.serializeUrl(this.router.createUrlTree(
      ['/dashboard/enrollments/corporate/edit-participant', id, participantId], { queryParams: { returnTo: viewUrl } }));
    const win = window.open('', '_blank');
    const open = () => { if (win) win.location.href = url; else this.router.navigateByUrl(url); };
    if (!this.isSuperAdmin) { open(); return; }
    this.recalculatingParticipantId = participantId;
    this.adminService.recalculateAll(id, 'participants', participantId).subscribe({
      next: (resp: any) => {
        const changed = Number((resp?.data ?? resp)?.totalChanged ?? 0);
        if (changed > 0) this.toast.showToast('info', `Se actualizaron ${changed} cuota(s) del participante.`);
        this.recalculatingParticipantId = null; open();
      },
      error: () => {
        this.recalculatingParticipantId = null;
        this.toast.showToast('warning', 'No se pudieron recalcular los pagos; abriendo el editor igualmente.'); open();
      }
    });
  }

  goToAddParticipants(): void {
    if (!this.details?.agreementId) return;
    this.router.navigate(['/dashboard/enrollments/corporate/add-participants', this.details.agreementId]);
  }

  refinanceParticipants(): void {
    // Obsoleto: navegación a pantalla dedicada
    if (this.selectedParticipant) {
      this.goToEditParticipantPlans(this.selectedParticipant);
    }
  }

  private loadDetails(): void {
    if (!this.agreementId) { return; }
    this.loading.show();
    this.enrollmentService.getCorporateDetails(this.agreementId).subscribe({
      next: (res: any) => {
        this.details = res.data || res; // backend devuelve Result o dto directo

        // Normalizar y ordenar datos para la vista
        if (this.details) {
          if (Array.isArray(this.details.companyPaymentPlans)) {
            this.details.companyPaymentPlans = [...this.details.companyPaymentPlans]
              .sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
              .map((p: any) => ({ ...p, isCollapsed: true }));
          }
          if (Array.isArray(this.details.participantEnrollments)) {
            // 🆕 Cargar última cotización vigente si es inscripción internacional
            const currencyCode = this.getEnrollmentCurrencyCode();
            if (this.isInternationalEnrollment && currencyCode && currencyCode !== 'COP') {
              this.loadCurrentExchangeRate();
            }
            this.details.participantEnrollments = this.details.participantEnrollments.map((pe: any) => ({
              ...pe,
              isCollapsed: true,
              paymentPlans: Array.isArray(pe.paymentPlans)
                ? [...pe.paymentPlans]
                  .sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
                  .map((p: any) => ({ ...p, isCollapsed: true }))
                : []
            }));
          }
        }
        // Inicializar el estado seleccionado del dropdown
        this.initSelectedEnrollmentStatus();
        // 🆕 Contexto de matrícula (banner divergencia + modal) y papelera
        this.loadInscriptionContext();
        this.loadTrashCount();
        this.loading.hide();
      },
      error: (err) => {
        console.error(err);
        this.toast.showToast('error', 'No se pudo cargar el detalle corporativo');
        this.loading.hide();
      }
    });
  }

  // 🆕 ====== Valor de matrícula por estudiante (contexto + modal + banner) ======
  /** Contexto de matrícula (solo admins; alimenta banner + modal). */
  inscriptionContext: any = null;

  loadInscriptionContext(): void {
    if (!this.isSuperAdmin || !this.details?.agreementId) return;
    this.corporateService.getInscriptionContext(this.details.agreementId).subscribe({
      next: (resp: any) => { this.inscriptionContext = resp?.data ?? resp; },
      error: () => { /* silencioso: el banner simplemente no se evalúa */ }
    });
  }

  /** 🆕 Banner: solo si el valor por participante fue editado alguna vez (≠ null). */
  get corporateNeedsRedistribution(): { needs: boolean; expected: number; sumActive: number; delta: number } {
    const ctx = this.inscriptionContext;
    if (!ctx || ctx.perParticipantAmount == null) return { needs: false, expected: 0, sumActive: 0, delta: 0 };
    const expected = Number(ctx.perParticipantAmount) * Number(ctx.activeParticipantsCount ?? 0);
    const sumActive = this.getCompanyTotalAmount() + this.getTotalParticipantsAmount();
    const delta = expected - sumActive;
    return { needs: Math.abs(delta) > 0.01, expected, sumActive, delta };
  }

  editAmountModal: {
    open: boolean; loading: boolean; saving: boolean; confirmStep: boolean;
    context: any | null; newValue: number | null; comment: string;
  } = { open: false, loading: false, saving: false, confirmStep: false, context: null, newValue: null, comment: '' };

  openEditAmountModal(): void {
    if (!this.details?.agreementId) return;
    this.editAmountModal = { open: true, loading: true, saving: false, confirmStep: false, context: null, newValue: null, comment: '' };
    this.corporateService.getInscriptionContext(this.details.agreementId).subscribe({
      next: (resp: any) => {
        const ctx = resp?.data ?? resp;
        this.editAmountModal.context = ctx;
        this.editAmountModal.newValue = Number(ctx?.perParticipantAmount ?? ctx?.referenceAmount ?? 0);
        this.editAmountModal.loading = false;
      },
      error: () => {
        this.editAmountModal.open = false;
        this.toast.showToast('error', 'No se pudo cargar el valor de referencia del proyecto.');
      }
    });
  }

  closeEditAmountModal(): void {
    if (this.editAmountModal.saving) return;
    this.editAmountModal.open = false;
  }

  /** 🆕 Preview en vivo del descuento resultante (por participante). El máximo del proyecto es solo informativo. */
  get editAmountPreview(): { discount: number; discountAmount: number; aboveReference: boolean } {
    const ctx = this.editAmountModal.context;
    const val = Number(this.editAmountModal.newValue ?? 0);
    const ref = Number(ctx?.referenceAmount ?? 0);
    if (!ctx || ref <= 0 || val <= 0) return { discount: 0, discountAmount: 0, aboveReference: false };
    const discountAmount = Math.max(0, ref - val);
    const discount = Math.round((discountAmount / ref) * 10000) / 100;
    return { discount, discountAmount, aboveReference: val > ref };
  }

  /** 🆕 Total estimado del acuerdo: valor por estudiante × participantes activos. */
  get editAmountEstimatedTotal(): number {
    const n = Number(this.editAmountModal.context?.activeParticipantsCount ?? 0);
    return Number(this.editAmountModal.newValue ?? 0) * n;
  }

  /** Paso 1: valida y pasa a la confirmación. */
  confirmEditAmount(): void {
    const val = Number(this.editAmountModal.newValue ?? 0);
    if (val <= 0) { this.toast.showToast('error', 'El nuevo valor debe ser mayor a 0.'); return; }
    if ((this.editAmountModal.comment ?? '').trim().length < 10) {
      this.toast.showToast('error', 'Comentario obligatorio (mín. 10 caracteres) para audit trail.');
      return;
    }
    this.editAmountModal.confirmStep = true;
  }

  /** Paso 2: guarda definitivamente. */
  saveEditAmount(): void {
    if (this.editAmountModal.saving || !this.details?.agreementId) return;
    this.editAmountModal.saving = true;
    this.corporateService.updateInscriptionAmount(
      this.details.agreementId,
      Number(this.editAmountModal.newValue),
      this.editAmountModal.comment.trim()
    ).subscribe({
      next: () => {
        this.editAmountModal.saving = false;
        this.editAmountModal.open = false;
        this.toast.showToast('success',
          'Valor de matrícula actualizado. Recuerda editar el plan de pagos para redistribuir los saldos de las cuotas.');
        this.loadDetails();
      },
      error: (err: any) => {
        this.editAmountModal.saving = false;
        this.editAmountModal.confirmStep = false;
        const msg = typeof err?.error === 'string' ? err.error : (err?.error?.message ?? 'No se pudo actualizar el valor.');
        this.toast.showToast('error', msg);
      }
    });
  }

  // 🆕 ====== Papelera de cuotas eliminadas (permiso ENROLLMENTS_PAYMENT_PLAN_TRASH) ======
  get canSeeTrash(): boolean {
    return this.authorizationService.hasPermission('ENROLLMENTS_PAYMENT_PLAN_TRASH');
  }

  trashModal: {
    open: boolean; loading: boolean; items: any[]; count: number;
    restoring: any | null; comment: string; saving: boolean;
  } = { open: false, loading: false, items: [], count: 0, restoring: null, comment: '', saving: false };

  trashTab: 'company' | 'participants' = 'company';

  get trashCompanyItems(): any[] {
    return this.trashModal.items.filter((i: any) => i.participantId == null);
  }

  get trashParticipantItems(): any[] {
    return this.trashModal.items.filter((i: any) => i.participantId != null);
  }

  /** Nombre del participante para una cuota de papelera (mapea contra los details cargados). */
  getTrashParticipantName(item: any): string {
    const pe = (this.details?.participantEnrollments ?? [])
      .find((x: any) => x?.enrollment?.participant?.id === item.participantId);
    const p = pe?.enrollment?.participant;
    return p ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() : `Participante #${item.participantId}`;
  }

  loadTrashCount(): void {
    if (!this.canSeeTrash || !this.details?.agreementId) return;
    this.adminService.getTrash(this.details.agreementId).subscribe({
      next: (resp: any) => {
        const items = (resp?.data ?? resp) ?? [];
        this.trashModal.items = Array.isArray(items) ? items : [];
        this.trashModal.count = this.trashModal.items.length;
      },
      error: () => { /* papelera no disponible: silencioso */ }
    });
  }

  openTrashModal(): void {
    this.trashModal.open = true;
    this.trashModal.restoring = null;
    this.trashModal.comment = '';
    this.trashTab = 'company';
    this.loadTrashCount();
  }

  askRestore(item: any): void {
    this.trashModal.restoring = item;
    this.trashModal.comment = '';
  }

  confirmRestore(): void {
    if (!this.trashModal.restoring || this.trashModal.saving) return;
    if ((this.trashModal.comment ?? '').trim().length < 10) {
      this.toast.showToast('error', 'Comentario obligatorio (mín. 10 caracteres) para audit trail.');
      return;
    }
    this.trashModal.saving = true;
    this.adminService.restore(this.trashModal.restoring.id, this.trashModal.comment.trim()).subscribe({
      next: (resp: any) => {
        const data = resp?.data ?? resp;
        this.trashModal.saving = false;
        this.trashModal.restoring = null;
        this.toast.showToast('success', data?.message ?? 'Cuota restaurada.');
        this.loadDetails();
      },
      error: (err: any) => {
        this.trashModal.saving = false;
        const msg = typeof err?.error === 'string' ? err.error : 'No se pudo restaurar la cuota.';
        this.toast.showToast('error', msg);
        this.loadTrashCount();
      }
    });
  }

  // === MÉTODOS FINANCIEROS ===

  // Resumen empresa
  getCompanyCompletedPayments(): number {
    return this.details?.companyPaymentPlans?.filter((p: any) => p.paymentStatus === 'PAID').length || 0;
  }

  getCompanyPendingPayments(): number {
    return this.details?.companyPaymentPlans?.filter((p: any) =>
      ['PENDING', 'OVERDUE'].includes(p.paymentStatus)).length || 0;
  }

  getCompanyTotalAmount(): number {
    return this.details?.companyPaymentPlans?.reduce((acc: number, plan: any) =>
      acc + (plan.amountToPay || 0), 0) || 0;
  }

  getCompanyPaidAmount(): number {
    return this.details?.companyPaymentPlans
      ?.reduce((acc: number, plan: any) => acc + (plan.amountPaid || 0), 0) || 0;
  }

  /** Redondeo a 2 decimales. */
  private roundTwo(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  /** Matrícula ancla de empresa; fallback a suma de cuotas si el backend no la envía. */
  getCompanyExpectedTotal(): number {
    const anchor = Number(this.details?.companyExpectedTotal ?? 0);
    return anchor > 0 ? anchor : this.getCompanyTotalAmount();
  }

  /** Faltante de empresa por reasignar = ancla − suma de cuotas activas (>= 0). */
  getCompanyUnassignedAmount(): number {
    return Math.max(0, this.roundTwo(this.getCompanyExpectedTotal() - this.getCompanyTotalAmount()));
  }

  /** Pendiente real de empresa = ancla − pagado (incluye faltante). */
  getCompanyExpectedPending(): number {
    return Math.max(0, this.roundTwo(this.getCompanyExpectedTotal() - this.getCompanyPaidAmount()));
  }

  getCompanyPendingAmount(): number {
    return this.details?.companyPaymentPlans
      ?.filter((p: any) => p.paymentStatus !== 'PAID' && !this.isPaymentOverdue(p) && !p.isPunished)
      ?.reduce((acc: number, plan: any) => acc + (plan.amountToPay - (plan.amountPaid || 0)), 0) || 0;
  }

  getCompanyOverdueAmount(): number {
    return this.details?.companyPaymentPlans
      ?.filter((p: any) => this.isPaymentOverdue(p) && !p.isPunished)
      ?.reduce((acc: number, plan: any) => acc + (plan.amountToPay - (plan.amountPaid || 0)), 0) || 0;
  }

  getCompanyCastigadoAmount(): number {
    return this.details?.companyPaymentPlans
      ?.filter((p: any) => p.isPunished)
      ?.reduce((acc: number, plan: any) => acc + (plan.amountToPay - (plan.amountPaid || 0)), 0) || 0;
  }

  // Resumen participantes
  getTotalParticipantsAmount(): number {
    let total = 0;
    this.details?.participantEnrollments?.forEach((pe: any) => {
      total += pe.paymentPlans?.reduce((acc: number, plan: any) =>
        acc + (plan.amountToPay || 0), 0) || 0;
    });
    return total;
  }

  getTotalParticipantsPaid(): number {
    let total = 0;
    this.details?.participantEnrollments?.forEach((pe: any) => {
      total += pe.paymentPlans
        ?.reduce((acc: number, plan: any) => acc + (plan.amountPaid || 0), 0) || 0;
    });
    return total;
  }

  getTotalParticipantsPending(): number {
    let total = 0;
    this.details?.participantEnrollments?.forEach((pe: any) => {
      total += pe.paymentPlans?.filter((p: any) => p.paymentStatus !== 'PAID' && !this.isPaymentOverdue(p) && !p.isPunished)
        ?.reduce((acc: number, plan: any) => acc + (plan.amountToPay - (plan.amountPaid || 0)), 0) || 0;
    });
    return total;
  }

  getTotalParticipantsCastigado(): number {
    let total = 0;
    this.details?.participantEnrollments?.forEach((pe: any) => {
      total += pe.paymentPlans?.filter((p: any) => p.isPunished)
        ?.reduce((acc: number, plan: any) => acc + (plan.amountToPay - (plan.amountPaid || 0)), 0) || 0;
    });
    return total;
  }

  getTotalCastigadoValue(): number {
    return this.getCompanyCastigadoAmount() + this.getTotalParticipantsCastigado();
  }

  /** Total vencido sumado de todos los participantes (no castigado). */
  getTotalParticipantsOverdue(): number {
    let total = 0;
    this.details?.participantEnrollments?.forEach((pe: any) => {
      total += this.overdueForParticipant(pe) || 0;
    });
    return total;
  }

  /** Alias semántico para Resumen — cuotas empresa pagadas. */
  getCompanyCompletedPaymentsCount(): number {
    return this.getCompanyCompletedPayments();
  }

  // Métodos para participante específico
  getParticipantCompletedPayments(participantPlans: any[]): number {
    return participantPlans?.filter((p: any) => p.paymentStatus === 'PAID').length || 0;
  }

  getParticipantPendingPayments(participantPlans: any[]): number {
    return participantPlans?.filter((p: any) =>
      ['PENDING', 'OVERDUE'].includes(p.paymentStatus)).length || 0;
  }

  getParticipantTotalAmount(participantPlans: any[]): number {
    return participantPlans?.reduce((acc: number, plan: any) =>
      acc + (plan.amountToPay || 0), 0) || 0;
  }

  getParticipantOverdueAmount(participantPlans: any[]): number {
    return participantPlans?.filter((p: any) => this.isPaymentOverdue(p) && !p.isPunished)
      ?.reduce((acc: number, plan: any) => acc + (plan.amountToPay - (plan.amountPaid || 0)), 0) || 0;
  }

  getParticipantCastigadoAmount(participantPlans: any[]): number {
    return participantPlans?.filter((p: any) => p.isPunished)
      ?.reduce((acc: number, plan: any) => acc + (plan.amountToPay - (plan.amountPaid || 0)), 0) || 0;
  }

  // === MÉTODOS DE ESTADO DE PAGOS ===

  isPaymentOverdue(plan: any): boolean {
    const dueDate = new Date(plan.dueDate);
    return dueDate < this.today && plan.paymentStatus !== 'PAID';
  }

  getDaysOverdue(plan: any): number {
    const dueDate = new Date(plan.dueDate);
    const diffTime = Math.abs(this.today.getTime() - dueDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  getPaymentStatusClass(status: string) {
    return {
      'bg-white': true,
      'bg-green-50': status === 'PAID',
      'bg-yellow-50': status === 'PENDING',
      'bg-red-50': status === 'OVERDUE' || status === 'CANCELED'
    };
  }

  getPaymentStatusLabel(status: string): string {
    switch (status) {
      case "PENDING":
        return 'Pendiente';
      case "PAID":
        return 'Pagado';
      case "OVERDUE":
        return 'Vencido';
      case "PARTIALLY_PAID":
        return 'Parcialmente Pagado';
      case "LINK_GENERATED":
        return 'Link generado';
      case "CANCELED":
        return 'Cancelado';
      default:
        return 'Desconocido';
    }
  }

  // === MÉTODOS DE UI ===

  toggleCollapse(plan: any) {
    plan.isCollapsed = !plan.isCollapsed;
    if (typeof plan.isCollapsed === 'undefined') {
      plan.isCollapsed = false;
    }
  }

  toggleParticipantCollapse(participant: any) {
    participant.isCollapsed = !participant.isCollapsed;
    if (typeof participant.isCollapsed === 'undefined') {
      participant.isCollapsed = false;
    }
  }

  // === MÉTODOS DE FORMATO ===

  formatCurrency(value: number, symbol: string = '$', code: string = 'COP'): string {
    if (value == null || isNaN(value)) return '';
    const formatted = new Intl.NumberFormat('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    return `${symbol}${formatted} (${code})`;
  }

  /**
   * Formatea un valor monetario usando la moneda de la inscripción corporativa (no del proyecto)
   * Para inscripciones internacionales usa USD/EUR, para nacionales usa COP
   */
  formatCurrencyValue(amount: number, symbol?: string, code?: string): string {
    if (!amount && amount !== 0) return '';

    // 🆕 Prioridad: parámetros > moneda de inscripción corporativa > moneda del proyecto > COP
    const currencySymbol = symbol || this.getEnrollmentCurrencySymbol();
    const currencyCode = code || this.getEnrollmentCurrencyCode();

    // Formatear el número con separador de miles
    const formattedAmount = new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    // Retornar con símbolo y código
    return `${currencySymbol}${formattedAmount} (${currencyCode})`;
  }

  // === MÉTODOS PARA MONEDA DE INSCRIPCIÓN CORPORATIVA ===

  /**
   * 🆕 Verifica si la inscripción corporativa es internacional
   */
  get isInternationalEnrollment(): boolean {
    return this.details?.isInternational === true;
  }

  /**
   * 🆕 Obtiene el código de moneda de la inscripción corporativa
   * Prioridad: moneda de inscripción > moneda del proyecto > COP
   */
  getEnrollmentCurrencyCode(): string {
    if (this.details?.currencyCode) {
      return this.details.currencyCode;
    }
    return this.details?.project?.currency?.code || 'COP';
  }

  /**
   * 🆕 Obtiene el símbolo de moneda de la inscripción corporativa
   */
  getEnrollmentCurrencySymbol(): string {
    const code = this.getEnrollmentCurrencyCode();
    switch (code) {
      case 'USD': return 'US$';
      case 'EUR': return '€';
      case 'COP':
      default: return '$';
    }
  }

  /**
   * 🆕 Obtiene la tasa de cambio de la inscripción corporativa
   */
  getEnrollmentExchangeRate(): number | null {
    return this.details?.exchangeRate || null;
  }

  /**
   * 🆕 Calcula el equivalente en COP de un monto para inscripciones internacionales
   */
  calculateCOPEquivalent(amount: number): string {
    if (!this.isInternationalEnrollment) return '';

    const rate = this.getEnrollmentExchangeRate();
    if (!rate) return 'N/A';

    const copAmount = amount * rate;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(copAmount);
  }

  /**
   * 🆕 Formatea la tasa de cambio para mostrar
   */
  formatExchangeRate(): string {
    const rate = this.getEnrollmentExchangeRate();
    if (!rate) return 'N/A';
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(rate);
  }

  // === MÉTRICAS GENERALES ===

  getTotalEnrollmentValue(): number {
    return this.getCompanyTotalAmount() + this.getTotalParticipantsAmount();
  }

  getTotalPaidValue(): number {
    return this.getCompanyPaidAmount() + this.getTotalParticipantsPaid();
  }

  getTotalPendingValue(): number {
    return this.getCompanyPendingAmount() + this.getTotalParticipantsPending();
  }

  getPaymentCompletionPercentage(): number {
    const total = this.getTotalEnrollmentValue();
    const paid = this.getTotalPaidValue();
    return total > 0 ? (paid / total) * 100 : 0;
  }

  // === MÉTODOS DE OFFCANVAS ===

  openPaymentPlansOffcanvas(participant: any): void {
    this.selectedParticipant = participant;
    this.isOffcanvasOpen = true;
    // Prevenir scroll del body cuando el offcanvas está abierto
    document.body.style.overflow = 'hidden';
  }

  closePaymentPlansOffcanvas(): void {
    this.isOffcanvasOpen = false;
    this.selectedParticipant = null;
    // Restaurar scroll del body
    document.body.style.overflow = 'auto';
  }

  // Cerrar offcanvas al hacer click en el overlay
  onOffcanvasOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.closePaymentPlansOffcanvas();
    }
  }

  // Cerrar con tecla Escape
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOffcanvasOpen) {
      this.closePaymentPlansOffcanvas();
    }
  }

  // === MÉTODOS AUXILIARES PARA LA TABLA ===

  trackByParticipant(index: number, item: any): any {
    return item.enrollment?.participant?.id || index;
  }

  trackByPaymentPlan(index: number, item: any): any {
    return item.id || index;
  }

  getNextDuePlan(paymentPlans: any[]): any | null {
    if (!paymentPlans || !paymentPlans.length) return null;

    const pendingPlans = paymentPlans
      .filter(p => ['PENDING', 'OVERDUE'].includes(p.paymentStatus))
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());

    return pendingPlans.length > 0 ? pendingPlans[0] : null;
  }

  getDaysUntilDue(plan: any): number {
    if (!plan || !plan.dueDate) return 0;
    const dueDate = new Date(plan.dueDate);
    const today = new Date();
    const diffTime = dueDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // === MÉTODOS AUXILIARES PARA TEMPLATE ===

  getParticipantPaidPlansAmount(paymentPlans: any[]): number {
    if (!paymentPlans || !paymentPlans.length) return 0;
    const paidPlans = paymentPlans.filter(p => p.paymentStatus === 'PAID');
    return this.getParticipantTotalAmount(paidPlans);
  }

  getParticipantPendingPlansAmount(paymentPlans: any[]): number {
    if (!paymentPlans || !paymentPlans.length) return 0;
    const pendingPlans = paymentPlans.filter(p => ['PENDING', 'OVERDUE'].includes(p.paymentStatus) && !p.isPunished);
    return pendingPlans.reduce((acc: number, plan: any) =>
      acc + (plan.amountToPay - (plan.amountPaid || 0)), 0);
  }

  getParticipantCompletionPercentage(paymentPlans: any[]): number {
    if (!paymentPlans || !paymentPlans.length) return 0;
    const completedPayments = this.getParticipantCompletedPayments(paymentPlans);
    return (completedPayments / paymentPlans.length) * 100;
  }

  /**
   * Maneja la desactivación de un payment link de empresa
   * Recarga los datos para reflejar los cambios
   */
  onCompanyPaymentLinkDeactivated(): void {
    this.loadDetails();
  }

  /**
   * Maneja la desactivación de un payment link de participante
   * Recarga los datos para reflejar los cambios
   */
  onParticipantPaymentLinkDeactivated(): void {
    this.loadDetails();
  }

  // ============== MÉTODOS PARA HISTORIAL DE MOVIMIENTOS ==============

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  /**
   * 🆕 Carga el historial de movimientos de cartera corporativa
   */
  loadMovementHistory(): void {
    if (!this.agreementId) return;

    this.corporatePortfolioService.getCorporateMovementHistory(this.agreementId).subscribe({
      next: (response) => {
        this.movementHistory = this.extractData(response);
        console.log('📋 Historial de movimientos corporativo cargado:', this.movementHistory.length);
      },
      error: (err) => {
        console.error('Error al cargar historial de movimientos corporativo:', err);
        this.movementHistory = [];
      }
    });
  }

  /**
   * 🆕 Carga las devoluciones relacionadas con la inscripción corporativa
   */
  loadRefundVouchers(): void {
    if (!this.agreementId) return;

    // Cargar devoluciones por corporateEnrollmentId
    // Filtrar por corporateEnrollmentId o por payerType y corporateEnrollmentId
    this.refundVouchersService.getAll().subscribe({
      next: (response) => {
        if (response.isSuccess && response.data) {
          const allRefunds = Array.isArray(response.data) ? response.data : [];
          console.log('📋 Total devoluciones cargadas:', allRefunds.length);
          console.log('📋 AgreementId buscado:', this.agreementId);

          // Filtrar devoluciones relacionadas con esta inscripción corporativa
          // Buscar por corporateEnrollmentId directo o por payerType (COMPANY o PARTICIPANT_CORPORATE)
          this.refundVouchers = allRefunds.filter((refund: any) => {
            // Verificar corporateEnrollmentId directo
            if (refund.corporateEnrollmentId === this.agreementId) {
              return true;
            }
            // Verificar si es una devolución corporativa (COMPANY o PARTICIPANT_CORPORATE)
            // y si los items están relacionados con esta inscripción corporativa
            if (refund.payerType === 'COMPANY' || refund.payerType === 'PARTICIPANT_CORPORATE') {
              // Si tiene items, verificar si están relacionados con esta inscripción
              if (refund.refundVoucherItems && refund.refundVoucherItems.length > 0) {
                // Los items pueden tener referencias a incomeReceipts que pertenecen a esta inscripción
                return true; // Por ahora incluimos todas las devoluciones corporativas
              }
            }
            return false;
          });

          console.log('📋 Devoluciones corporativas filtradas:', this.refundVouchers.length);
          console.log('📋 Devoluciones encontradas:', this.refundVouchers.map((r: any) => ({
            id: r.id,
            documentNumber: r.documentNumber,
            corporateEnrollmentId: r.corporateEnrollmentId,
            payerType: r.payerType,
            status: r.status
          })));
        } else {
          this.refundVouchers = [];
        }
      },
      error: (err) => {
        console.error('Error al cargar devoluciones corporativas:', err);
        this.refundVouchers = [];
      }
    });
  }

  /**
   * 🆕 Verifica si hay historial de movimientos
   */
  hasMovementHistory(): boolean {
    return this.movementHistory && this.movementHistory.length > 0;
  }

  /**
   * 🆕 Obtiene el icono del tipo de movimiento
   */
  getMovementIcon(movementType: number | string): string {
    const type = Number(movementType);
    switch (type) {
      case 1: return '⚠️';  // Withdrawal
      case 2: return '✅';  // WithdrawalReversal
      case 3: return '⏸️';  // Deferral
      case 4: return '▶️';  // ContinueFromDeferral
      default: return '📋';
    }
  }

  /**
   * 🆕 Obtiene la clase CSS del tipo de movimiento
   */
  getMovementClass(movementType: number | string): string {
    const type = Number(movementType);
    switch (type) {
      case 1: return 'bg-red-100 border-red-400 text-red-800';  // Withdrawal
      case 2: return 'bg-green-100 border-green-400 text-green-800';  // WithdrawalReversal
      case 3: return 'bg-orange-100 border-orange-400 text-orange-800';  // Deferral
      case 4: return 'bg-blue-100 border-blue-400 text-blue-800';  // ContinueFromDeferral
      default: return 'bg-gray-100 border-gray-400 text-gray-800';
    }
  }

  /**
   * 🆕 Obtiene el color del punto en la timeline
   */
  getTimelineDotClass(movementType: number | string): string {
    const type = Number(movementType);
    switch (type) {
      case 1: return 'bg-red-500';  // Withdrawal
      case 2: return 'bg-green-500';  // WithdrawalReversal
      case 3: return 'bg-orange-500';  // Deferral
      case 4: return 'bg-blue-500';  // ContinueFromDeferral
      default: return 'bg-gray-500';
    }
  }

  // ============== MÉTODOS PARA CARTERA CASTIGADA ==============

  /**
   * Verifica si la inscripción corporativa tiene cuotas castigadas (empresa)
   */
  hasCompanyPunishedInstallments(): boolean {
    if (!this.details?.companyPaymentPlans) return false;
    return this.details.companyPaymentPlans.some((p: any) => p.isPunished);
  }

  /**
   * Obtiene las cuotas castigadas de la empresa
   */
  getCompanyPunishedInstallments(): any[] {
    if (!this.details?.companyPaymentPlans) return [];
    return this.details.companyPaymentPlans.filter((p: any) => p.isPunished);
  }

  /**
   * Calcula el total de cartera castigada de la empresa
   */
  getTotalCompanyPunishedAmount(): number {
    return this.getCompanyPunishedInstallments()
      .reduce((sum: number, p: any) => sum + (p.amountToPay - (p.amountPaid || 0)), 0);
  }

  /**
   * Verifica si un participante tiene cuotas castigadas
   */
  hasParticipantPunishedInstallments(participant: any): boolean {
    if (!participant?.paymentPlans) return false;
    return participant.paymentPlans.some((p: any) => p.isPunished);
  }

  /**
   * Obtiene las cuotas castigadas de un participante
   */
  getParticipantPunishedInstallments(participant: any): any[] {
    if (!participant?.paymentPlans) return [];
    return participant.paymentPlans.filter((p: any) => p.isPunished);
  }

  /**
   * Calcula el total de cartera castigada de un participante
   */
  getTotalParticipantPunishedAmount(participant: any): number {
    return this.getParticipantPunishedInstallments(participant)
      .reduce((sum: number, p: any) => sum + (p.amountToPay - (p.amountPaid || 0)), 0);
  }

  /**
   * Verifica si hay cuotas castigadas en total (empresa + participantes)
   */
  hasTotalPunishedInstallments(): boolean {
    if (this.hasCompanyPunishedInstallments()) return true;

    if (this.details?.participantEnrollments) {
      return this.details.participantEnrollments.some((pe: any) =>
        this.hasParticipantPunishedInstallments(pe)
      );
    }

    return false;
  }

  /**
   * Calcula el total general de cartera castigada (empresa + participantes)
   */
  getTotalPunishedAmount(): number {
    let total = this.getTotalCompanyPunishedAmount();

    if (this.details?.participantEnrollments) {
      this.details.participantEnrollments.forEach((pe: any) => {
        total += this.getTotalParticipantPunishedAmount(pe);
      });
    }

    return total;
  }

  /**
   * Obtiene la etiqueta del tipo de movimiento
   */
  getMovementTypeLabel(movementType: number | string): string {
    const typeMap: { [key: string]: string } = {
      '0': 'Sin movimiento',
      '1': 'Retiro',
      '2': 'Reversión de Retiro',
      '3': 'Aplazamiento',
      '4': 'Continuación de Aplazamiento',
      'None': 'Sin movimiento',
      'Withdrawal': 'Retiro',
      'WithdrawalReversal': 'Reversión de Retiro',
      'Deferral': 'Aplazamiento',
      'ContinueFromDeferral': 'Continuación de Aplazamiento'
    };
    return typeMap[String(movementType)] || 'Desconocido';
  }

  /**
   * Verifica si la inscripción corporativa está retirada o aplazada
   */
  isEnrollmentWithdrawnOrDeferred(): boolean {
    return this.isEnrollmentWithdrawn || this.isEnrollmentDeferred;
  }

  // === MÉTODOS PARA TASA DE CAMBIO EN RECIBOS ===

  /**
   * 🆕 Obtiene la tasa de cambio correcta para un plan de pago
   * Prioridad: tasa del comprobante (si está pagado) > última cotización vigente (si está pendiente) > tasa de la inscripción
   */
  getPlanExchangeRate(plan: any): number | null {
    if (!this.isInternationalEnrollment) return null;

    // Si el plan está pagado y tiene comprobantes, usar la tasa del primer comprobante (histórica)
    if (plan.paymentStatus === 'PAID' && plan.incomeReceipts && plan.incomeReceipts.length > 0) {
      const receipt = plan.incomeReceipts[0]; // Usar el primer comprobante
      if (receipt.exchangeRate && receipt.exchangeRate !== null && receipt.exchangeRate !== 1) {
        return receipt.exchangeRate;
      }
    }

    // 🆕 Para planes pendientes, usar la última cotización vigente si está disponible
    if (plan.paymentStatus !== 'PAID' && this.currentExchangeRate && this.currentExchangeRate !== 1) {
      return this.currentExchangeRate;
    }

    // Fallback: usar la tasa de la inscripción corporativa
    return this.getEnrollmentExchangeRate();
  }

  /**
   * 🆕 Carga la última cotización vigente para la moneda de la inscripción
   */
  private loadCurrentExchangeRate(): void {
    const currencyCode = this.getEnrollmentCurrencyCode();
    if (!currencyCode || currencyCode === 'COP') {
      this.currentExchangeRate = 1;
      return;
    }

    this.exchangeRateService.getLatestRate(currencyCode, 'COP').subscribe({
      next: (response: any) => {
        if (response.isSuccess && response.rate > 0) {
          this.currentExchangeRate = response.rate;
          console.log(`✅ Última cotización vigente cargada: ${this.currentExchangeRate} para ${currencyCode}`);
        } else {
          // Fallback a tasa de la inscripción
          this.currentExchangeRate = this.getEnrollmentExchangeRate();
        }
      },
      error: (err) => {
        console.error('Error obteniendo última cotización vigente:', err);
        // Fallback a tasa de la inscripción
        this.currentExchangeRate = this.getEnrollmentExchangeRate();
      }
    });
  }

  /**
   * 🆕 Calcula el equivalente en COP de un monto para un plan de pago específico
   * Usa la tasa de cambio del comprobante si está pagado, o la de la inscripción si está pendiente
   */
  calculatePlanCOPEquivalent(plan: any, amount: number): string {
    if (!this.isInternationalEnrollment || !amount) return '';

    const rate = this.getPlanExchangeRate(plan);
    if (!rate) return 'N/A';

    const copAmount = amount * rate;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(copAmount);
  }

  /**
   * 🆕 Formatea la tasa de cambio de un plan de pago para mostrar
   */
  formatPlanExchangeRate(plan: any): string {
    const rate = this.getPlanExchangeRate(plan);
    if (!rate) return 'N/A';
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(rate);
  }

  /**
   * 🆕 Verifica si un recibo tiene tasa de cambio internacional
   */
  hasReceiptExchangeRate(receipt: any): boolean {
    return receipt?.exchangeRate && receipt.exchangeRate !== null && receipt.exchangeRate !== 1;
  }

  /**
   * 🆕 Obtiene el equivalente en COP de un monto para un recibo con tasa de cambio
   */
  getReceiptCOPEquivalent(receipt: any, amount: number): string {
    if (!this.hasReceiptExchangeRate(receipt) || !receipt?.exchangeRate) {
      return 'N/A';
    }

    const copAmount = amount * receipt.exchangeRate;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(copAmount);
  }
  // ============== TABS DE COMPROBANTES (EMPRESA Y PARTICIPANTE) ==============

  getActiveReceipts(plan: any): any[] {
    if (!plan?.incomeReceipts) return [];
    return plan.incomeReceipts.filter((r: any) => !r.isCancelled);
  }

  getCancelledReceipts(plan: any): any[] {
    if (!plan?.incomeReceipts) return [];
    return plan.incomeReceipts.filter((r: any) => r.isCancelled);
  }

  /**
   * Fecha del último pago recibido de un plan específico (max transactionDate/createdAt
   * sobre comprobantes activos).
   */
  getPlanLastPaymentDate(plan: any): Date | null {
    const receipts = this.getActiveReceipts(plan);
    if (!receipts.length) return null;
    let latest: number | null = null;
    for (const r of receipts) {
      const raw = r.transactionDate || r.createdAt;
      if (!raw) continue;
      const t = new Date(raw).getTime();
      if (Number.isNaN(t)) continue;
      if (latest === null || t > latest) latest = t;
    }
    return latest === null ? null : new Date(latest);
  }

  /**
   * Fecha del último pago recibido sobre una lista de planes (empresa o participante).
   */
  getLastPaymentDate(plans: any[] | null | undefined): Date | null {
    if (!plans?.length) return null;
    let latest: number | null = null;
    for (const plan of plans) {
      const d = this.getPlanLastPaymentDate(plan);
      if (!d) continue;
      const t = d.getTime();
      if (latest === null || t > latest) latest = t;
    }
    return latest === null ? null : new Date(latest);
  }

  getCompanyPlanTab(planId: number): string {
    if (this.companyPlanTabs[planId]) return this.companyPlanTabs[planId];
    const plan = this.details?.companyPaymentPlans?.find((p: any) => p.id === planId);
    if (!plan) return 'active';
    if (this.isEnrollmentCancelled && this.getCancelledReceipts(plan).length > 0) {
      this.companyPlanTabs[planId] = 'cancelled';
      return 'cancelled';
    }
    this.companyPlanTabs[planId] = 'active';
    return 'active';
  }

  setCompanyPlanTab(planId: number, tab: string): void {
    this.companyPlanTabs[planId] = tab;
  }

  getParticipantPlanTab(planId: number): string {
    if (this.participantPlanTabs[planId]) return this.participantPlanTabs[planId];
    const plan = this.selectedParticipant?.paymentPlans?.find((p: any) => p.id === planId);
    if (!plan) return 'active';
    if (this.isEnrollmentCancelled && this.getCancelledReceipts(plan).length > 0) {
      this.participantPlanTabs[planId] = 'cancelled';
      return 'cancelled';
    }
    this.participantPlanTabs[planId] = 'active';
    return 'active';
  }

  setParticipantPlanTab(planId: number, tab: string): void {
    this.participantPlanTabs[planId] = tab;
  }

  // ============== EMAIL LOGS ==============

  loadEmailsForPlan(plan: any): void {
    if (!plan.paymentLinkId) return;

    this.emailsLoadingByPlan[plan.id] = true;

    this.emailLogService.searchEmailLogs({
      paymentLinkId: plan.paymentLinkId,
      pageSize: 50,
      orderBy: 'SentAt',
      orderDirection: 'desc'
    }).subscribe({
      next: (response) => {
        this.emailsByPlan[plan.id] = response.data;
        this.emailsLoadingByPlan[plan.id] = false;
      },
      error: (err) => {
        console.error('Error al cargar emails para plan', plan.id, err);
        this.emailsByPlan[plan.id] = [];
        this.emailsLoadingByPlan[plan.id] = false;
      }
    });
  }

  getEmailsForPlan(plan: any): EmailLogResponseDTO[] {
    return this.emailsByPlan[plan.id] || [];
  }

  hasEmails(plan: any): boolean {
    return this.emailsByPlan[plan.id] !== undefined && this.emailsByPlan[plan.id].length > 0;
  }

  isEmailsLoading(plan: any): boolean {
    return this.emailsLoadingByPlan[plan.id] === true;
  }

  // ============== GESTIÓN DE CASTIGO MANUAL (EMPRESA) ==============
  isCompanyPunishMode: boolean = false;
  selectedCompanyPlanIds: number[] = [];
  punishReason: string = '';
  punishNotes: string = '';

  toggleCompanyPunishMode(): void {
    if (!this.isCompanyPunishMode && this.areAllCompanyPaymentPlansPaid()) {
      return;
    }
    this.isCompanyPunishMode = !this.isCompanyPunishMode;
    this.selectedCompanyPlanIds = [];
    this.punishReason = '';
    this.punishNotes = '';
  }

  areAllParticipantPaymentPlansPaid(participant: any): boolean {
    const plans = participant?.paymentPlans;
    if (!plans || plans.length === 0) return false;
    return plans.every((p: any) => p.paymentStatus === 'PAID');
  }

  /**
   * Deshabilita "Editar Plan Empresa" SOLO cuando todas las cuotas empresa
   * están pagadas Y el acuerdo NO tiene una marca de needsAttention (que indica
   * que la suma de cuotas no cuadra con el total registrado — i.e. queda saldo
   * pendiente sin asignar a cuotas).
   */
  isEditCompanyPlanDisabled(): boolean {
    return this.areAllCompanyPaymentPlansPaid() && !this.details?.needsAttention;
  }

  /**
   * Idem por participante: si el acuerdo está marcado needsAttention, se permite
   * editar el plan del participante aunque sus cuotas estén todas pagadas, por
   * si el saldo pendiente corresponde a ese participante.
   */
  isEditParticipantPlanDisabled(participant: any): boolean {
    return this.areAllParticipantPaymentPlansPaid(participant) && !this.details?.needsAttention;
  }

  areAllCompanyPaymentPlansPaid(): boolean {
    const participants = this.details?.participantEnrollments;
    if (!participants || participants.length === 0) return false;
    let total = 0;
    for (const pe of participants) {
      const plans = pe?.paymentPlans;
      if (!plans || plans.length === 0) continue;
      total += plans.length;
      if (plans.some((p: any) => p.paymentStatus !== 'PAID')) {
        return false;
      }
    }
    return total > 0;
  }

  // 🆕 Modal de confirmación para toggle de cobro automático
  showAutoPaymentConfirmationModal: boolean = false;
  pendingAutoPaymentValue: boolean = false;
  autoPaymentConfirmationConfig: ConfirmationConfig = {
    title: '',
    message: '',
    type: 'warning',
    confirmText: '',
    cancelText: 'Cancelar',
    showIcon: true
  };

  /**
   * 🆕 Cambia el estado del cobro automático para inscripciones corporativas (pide confirmación antes de aplicar)
   */
  toggleAutoPayment(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const enabled = checkbox.checked;

    // Revertir el checkbox visualmente hasta que el usuario confirme
    checkbox.checked = !enabled;

    this.pendingAutoPaymentValue = enabled;
    this.autoPaymentConfirmationConfig = {
      title: enabled ? '¿Activar cobro automático?' : '¿Desactivar cobro automático?',
      message: enabled
        ? 'A partir de ahora el sistema enviará links de pago automáticamente por email para las cuotas pendientes. ¿Deseas continuar?'
        : 'Los pagos de las cuotas pendientes deberán gestionarse manualmente y no se enviarán links de pago por email. ¿Deseas continuar?',
      type: enabled ? 'info' : 'warning',
      confirmText: enabled ? 'Sí, activar' : 'Sí, desactivar',
      cancelText: 'Cancelar',
      showIcon: true,
      icon: enabled ? 'fa-envelope' : 'fa-envelope-open'
    };
    this.showAutoPaymentConfirmationModal = true;
  }

  onAutoPaymentConfirmed(): void {
    this.showAutoPaymentConfirmationModal = false;
    const enabled = this.pendingAutoPaymentValue;

    this.loading.show();
    this.enrollmentService.updateCorporateAutoPayment(this.agreementId, enabled).subscribe({
      next: () => {
        this.loading.hide();
        if (this.details) {
          this.details.autoGeneratePaymentLinks = enabled;
        }
        if (enabled) {
          this.toast.showSuccess('Cobro automático activado. Se enviarán links de pago por email.');
        } else {
          this.toast.showWarning('Cobro automático desactivado. Los pagos se gestionarán manualmente.');
        }
      },
      error: (err) => {
        this.loading.hide();
        this.toast.showError('Error al actualizar el cobro automático: ' + (err.message || 'Error desconocido'));
      }
    });
  }

  onAutoPaymentCancelled(): void {
    this.showAutoPaymentConfirmationModal = false;
  }

  toggleCompanyPlanSelection(planId: number): void {
    const plan = this.details.companyPaymentPlans.find((p: any) => p.id === planId);
    if (plan && plan.paymentStatus === 'PAID') {
      return;
    }

    if (this.selectedCompanyPlanIds.includes(planId)) {
      this.selectedCompanyPlanIds = this.selectedCompanyPlanIds.filter(id => id !== planId);
    } else {
      this.selectedCompanyPlanIds.push(planId);
    }
  }

  isCompanyPlanSelected(planId: number): boolean {
    return this.selectedCompanyPlanIds.includes(planId);
  }

  selectAllCompanyPlans(): void {
    if (this.details?.companyPaymentPlans) {
      const availablePlans = this.details.companyPaymentPlans.filter((p: any) => p.paymentStatus !== 'PAID');

      if (this.selectedCompanyPlanIds.length === availablePlans.length) {
        this.selectedCompanyPlanIds = [];
      } else {
        this.selectedCompanyPlanIds = availablePlans.map((p: any) => p.id);
      }
    }
  }

  executeCompanyPunishAction(action: 'punish' | 'unpunish'): void {
    if (this.selectedCompanyPlanIds.length === 0) {
      this.toast.showToast('warning', 'Debes seleccionar al menos una cuota.');
      return;
    }

    if (!this.punishReason || this.punishReason.trim().length === 0) {
      this.toast.showToast('warning', 'Debes ingresar una razón para esta acción.');
      return;
    }

    const request = {
      corporateEnrollmentId: this.agreementId,
      corporatePaymentPlanIds: this.selectedCompanyPlanIds,
      reason: this.punishReason,
      notes: this.punishNotes
    };

    this.loading.show();

    const observable = action === 'punish'
      ? this.corporatePortfolioService.punishCorporateInstallments(request)
      : this.corporatePortfolioService.unpunishCorporateInstallments(request);

    observable.subscribe({
      next: (response) => {
        if (response.isSuccess) {
          const message = response.data?.message || (action === 'punish' ? 'Cuotas castigadas exitosamente' : 'Cuotas descastigadas exitosamente');
          this.toast.showToast('success', message);
          this.toggleCompanyPunishMode();
          this.loadDetails(); // Recargar datos
        } else {
          this.toast.showToast('error', response.errorMessage || 'Ocurrió un error al procesar la solicitud.');
        }
        this.loading.hide();
      },
      error: (err) => {
        console.error(`Error al ${action === 'punish' ? 'castigar' : 'descastigar'} cuotas corporativas:`, err);
        this.toast.showToast('error', `Error al procesar la solicitud: ${err.message}`);
        this.loading.hide();
      }
    });
  }

  // ============== GESTIÓN DE CASTIGO MANUAL (PARTICIPANTE) ==============
  isParticipantPunishMode: boolean = false;
  selectedParticipantPlanIds: number[] = [];
  participantPunishReason: string = '';
  participantPunishNotes: string = '';

  toggleParticipantPunishMode(): void {
    this.isParticipantPunishMode = !this.isParticipantPunishMode;
    this.selectedParticipantPlanIds = [];
    this.participantPunishReason = '';
    this.participantPunishNotes = '';
  }

  toggleParticipantPlanSelection(planId: number): void {
    const plan = this.selectedParticipant.paymentPlans.find((p: any) => p.id === planId);
    if (plan && plan.paymentStatus === 'PAID') {
      return;
    }

    if (this.selectedParticipantPlanIds.includes(planId)) {
      this.selectedParticipantPlanIds = this.selectedParticipantPlanIds.filter(id => id !== planId);
    } else {
      this.selectedParticipantPlanIds.push(planId);
    }
  }

  isParticipantPlanSelected(planId: number): boolean {
    return this.selectedParticipantPlanIds.includes(planId);
  }

  selectAllParticipantPlans(): void {
    if (this.selectedParticipant?.paymentPlans) {
      const availablePlans = this.selectedParticipant.paymentPlans.filter((p: any) => p.paymentStatus !== 'PAID');

      if (this.selectedParticipantPlanIds.length === availablePlans.length) {
        this.selectedParticipantPlanIds = [];
      } else {
        this.selectedParticipantPlanIds = availablePlans.map((p: any) => p.id);
      }
    }
  }

  executeParticipantPunishAction(action: 'punish' | 'unpunish'): void {
    if (this.selectedParticipantPlanIds.length === 0) {
      this.toast.showToast('warning', 'Debes seleccionar al menos una cuota.');
      return;
    }

    if (!this.participantPunishReason || this.participantPunishReason.trim().length === 0) {
      this.toast.showToast('warning', 'Debes ingresar una razón para esta acción.');
      return;
    }

    const request = {
      corporateEnrollmentId: this.agreementId,
      corporatePaymentPlanIds: this.selectedParticipantPlanIds,
      reason: this.participantPunishReason,
      notes: this.participantPunishNotes
    };

    this.loading.show();

    const observable = action === 'punish'
      ? this.corporatePortfolioService.punishCorporateInstallments(request)
      : this.corporatePortfolioService.unpunishCorporateInstallments(request);

    observable.subscribe({
      next: (response) => {
        if (response.isSuccess) {
          const message = response.data?.message || (action === 'punish' ? 'Cuotas castigadas exitosamente' : 'Cuotas descastigadas exitosamente');
          this.toast.showToast('success', message);
          this.toggleParticipantPunishMode();
          this.loadDetails(); // Recargar datos globales
          // Necesitamos cerrar y reabrir el offcanvas o actualizar selectedParticipant
          // Como loadDetails recarga todo, details.participantEnrollments se actualiza.
          // Debemos buscar el participante actualizado y reasignarlo a selectedParticipant
          this.refreshSelectedParticipant();
        } else {
          this.toast.showToast('error', response.errorMessage || 'Ocurrió un error al procesar la solicitud.');
        }
        this.loading.hide();
      },
      error: (err) => {
        console.error(`Error al ${action === 'punish' ? 'castigar' : 'descastigar'} cuotas de participante:`, err);
        this.toast.showToast('error', `Error al procesar la solicitud: ${err.message}`);
        this.loading.hide();
      }
    });
  }

  // Ayuda a mantener el participante seleccionado actualizado tras recargar
  private refreshSelectedParticipant(): void {
    if (this.selectedParticipant && this.details?.participantEnrollments) {
      const updatedParticipant = this.details.participantEnrollments.find((p: any) =>
        p.enrollment.id === this.selectedParticipant.enrollment.id
      );
      if (updatedParticipant) {
        this.selectedParticipant = updatedParticipant;
      }
    }
  }

  // ============== MÉTODOS PARA DEVOLUCIONES ==============

  /**
   * 🆕 Obtiene las devoluciones relacionadas con un plan de pago de empresa
   */
  getRefundsForCompanyPlan(plan: any): any[] {
    if (!this.refundVouchers || this.refundVouchers.length === 0) return [];

    // Filtrar devoluciones de esta inscripción corporativa
    // Si tiene payerType, verificar que sea 'COMPANY', si no, usar corporateEnrollmentId
    const companyRefunds = this.refundVouchers.filter((refund: any) => {
      // Debe ser de esta inscripción corporativa
      if (refund.corporateEnrollmentId !== this.agreementId) return false;
      // Si tiene payerType, debe ser 'COMPANY', si no tiene payerType pero tiene corporateEnrollmentId, también incluirlo
      return !refund.payerType || refund.payerType === 'COMPANY';
    });

    // Si no hay incomeReceipts, devolver todas las devoluciones de empresa de esta inscripción
    if (!plan?.incomeReceipts || plan.incomeReceipts.length === 0) {
      return companyRefunds.map((refund: any) => ({
        ...refund,
        relatedItems: [],
        totalRefundedForPlan: refund.refundAmount || 0
      }));
    }

    const planReceiptIds = plan.incomeReceipts.map((r: any) => r.id);
    const relatedRefunds: any[] = [];

    companyRefunds.forEach((refund: any) => {

      // Si tiene items, verificar si están relacionados con este plan
      if (refund.refundVoucherItems && refund.refundVoucherItems.length > 0) {
        const relatedItems = refund.refundVoucherItems.filter((item: any) => {
          if (item.documentType === 'IncomeReceipt') {
            if (planReceiptIds.includes(item.documentId)) return true;
            if (item.incomeReceipts && item.incomeReceipts.length > 0) {
              return item.incomeReceipts.some((receipt: any) =>
                planReceiptIds.includes(receipt.incomeReceiptId) ||
                planReceiptIds.includes(receipt.id)
              );
            }
          }
          return false;
        });

        if (relatedItems.length > 0) {
          relatedRefunds.push({
            ...refund,
            relatedItems: relatedItems,
            totalRefundedForPlan: relatedItems.reduce((sum: number, item: any) => sum + (item.refundAmount || 0), 0)
          });
        }
      } else {
        // Si no tiene items pero es de esta inscripción corporativa, incluirla
        if (refund.corporateEnrollmentId === this.agreementId) {
          relatedRefunds.push({
            ...refund,
            relatedItems: [],
            totalRefundedForPlan: refund.refundAmount || 0
          });
        }
      }
    });

    return relatedRefunds;
  }

  /**
   * 🆕 Obtiene las devoluciones relacionadas con un plan de pago de participante
   */
  getRefundsForParticipantPlan(plan: any): any[] {
    if (!this.refundVouchers || this.refundVouchers.length === 0) return [];

    // Filtrar devoluciones de participante corporativo de esta inscripción
    const participantRefunds = this.refundVouchers.filter((refund: any) => {
      // Debe ser de esta inscripción corporativa
      if (refund.corporateEnrollmentId !== this.agreementId) return false;
      // Si tiene payerType, debe ser 'PARTICIPANT_CORPORATE'
      return refund.payerType === 'PARTICIPANT_CORPORATE';
    });

    // Si no hay incomeReceipts, devolver todas las devoluciones de participante de esta inscripción
    if (!plan?.incomeReceipts || plan.incomeReceipts.length === 0) {
      return participantRefunds.map((refund: any) => ({
        ...refund,
        relatedItems: [],
        totalRefundedForPlan: refund.refundAmount || 0
      }));
    }

    const planReceiptIds = plan.incomeReceipts.map((r: any) => r.id);
    const relatedRefunds: any[] = [];

    participantRefunds.forEach((refund: any) => {
      // Si tiene items, verificar si están relacionados con este plan
      if (refund.refundVoucherItems && refund.refundVoucherItems.length > 0) {
        const relatedItems = refund.refundVoucherItems.filter((item: any) => {
          if (item.documentType === 'IncomeReceipt') {
            if (planReceiptIds.includes(item.documentId)) return true;
            if (item.incomeReceipts && item.incomeReceipts.length > 0) {
              return item.incomeReceipts.some((receipt: any) =>
                planReceiptIds.includes(receipt.incomeReceiptId) ||
                planReceiptIds.includes(receipt.id)
              );
            }
          }
          return false;
        });

        if (relatedItems.length > 0) {
          relatedRefunds.push({
            ...refund,
            relatedItems: relatedItems,
            totalRefundedForPlan: relatedItems.reduce((sum: number, item: any) => sum + (item.refundAmount || 0), 0)
          });
        }
      } else {
        // Si no tiene items pero es de esta inscripción corporativa, incluirla
        if (refund.corporateEnrollmentId === this.agreementId) {
          relatedRefunds.push({
            ...refund,
            relatedItems: [],
            totalRefundedForPlan: refund.refundAmount || 0
          });
        }
      }
    });

    return relatedRefunds;
  }

  /**
   * 🆕 Verifica si un plan de empresa tiene devoluciones
   */
  hasRefundsForCompanyPlan(plan: any): boolean {
    // Verificar si hay devoluciones de empresa para esta inscripción corporativa
    const hasCompanyRefunds = this.refundVouchers.some((refund: any) => {
      if (refund.corporateEnrollmentId !== this.agreementId) return false;
      return !refund.payerType || refund.payerType === 'COMPANY';
    });

    // También verificar si hay devoluciones relacionadas con los incomeReceipts del plan
    const planRefunds = this.getRefundsForCompanyPlan(plan);

    return hasCompanyRefunds || planRefunds.length > 0;
  }

  /**
   * 🆕 Verifica si un plan de participante tiene devoluciones
   */
  hasRefundsForParticipantPlan(plan: any): boolean {
    return this.getRefundsForParticipantPlan(plan).length > 0;
  }

  /**
   * 🆕 Obtiene el total devuelto para un plan de empresa
   */
  getTotalRefundedForCompanyPlan(plan: any): number {
    const refunds = this.getRefundsForCompanyPlan(plan);
    return refunds.reduce((sum: number, refund: any) => sum + (refund.totalRefundedForPlan || 0), 0);
  }

  /**
   * 🆕 Obtiene el total devuelto para un plan de participante
   */
  getTotalRefundedForParticipantPlan(plan: any): number {
    const refunds = this.getRefundsForParticipantPlan(plan);
    return refunds.reduce((sum: number, refund: any) => sum + (refund.totalRefundedForPlan || 0), 0);
  }

  /**
   * 🆕 Obtiene los items de devolución para un comprobante específico
   */
  getRefundItemsForReceipt(receiptId: number): any[] {
    if (!this.refundVouchers || this.refundVouchers.length === 0) return [];

    const items: any[] = [];

    this.refundVouchers.forEach((refund: any) => {
      if (refund.refundVoucherItems && refund.refundVoucherItems.length > 0) {
        refund.refundVoucherItems.forEach((item: any) => {
          if (item.documentType === 'IncomeReceipt' &&
            item.incomeReceipts &&
            item.incomeReceipts.some((r: any) => r.incomeReceiptId === receiptId || r.id === receiptId)) {
            items.push({
              ...item,
              refundVoucher: refund
            });
          }
        });
      }
    });

    return items;
  }

  /**
   * 🆕 Obtiene el código de moneda de una devolución
   */
  getRefundCurrencyCode(refund: any): string {
    return refund.currency?.code || refund.currencyCode || 'COP';
  }

  /**
   * 🆕 Formatea un monto con la moneda de la devolución
   */
  formatRefundAmount(amount: number, refund: any): string {
    const code = this.getRefundCurrencyCode(refund);
    const symbol = code === 'USD' ? 'US$' : code === 'EUR' ? '€' : '$';
    return `${symbol}${amount.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${code})`;
  }

  // ============== MÉTODOS PARA CAMBIAR ESTADO ==============

  /**
   * Obtiene las opciones de estado disponibles
   */
  getEnrollmentStatusOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'Pending', label: 'Pendiente' },
      { value: 'Active', label: 'Activo' },
      { value: 'Withdrawn', label: 'Retirado' },
      { value: 'Deferred', label: 'Aplazado' },
      { value: 'Completed', label: 'Completado' },
      { value: 'Refinanced', label: 'Refinanciado' },
      { value: 'Reversed', label: 'Reversado' },
      { value: 'Refund', label: 'Reintegro' }
    ];
  }

  /**
   * Maneja el cambio de estado de la inscripción corporativa - Abre el modal de confirmación
   */
  onStatusChange(newStatus: string): void {
    if (!this.details) return;

    const currentStatus = this.enrollmentStatus;

    if (currentStatus === newStatus) {
      return; // No hay cambio
    }

    const statusLabel = this.enrollmentStatusOptions.find(opt => opt.value === newStatus)?.label || 'Desconocido';

    // Guardar el cambio pendiente
    this.pendingStatusChange = { newStatus, statusLabel };
    this.statusChangeReason = '';

    // Configurar si se requiere razón (Withdrawn, Deferred o Cesión de Cupo)
    this.showReasonInput = (newStatus === 'Withdrawn' || newStatus === 'Deferred' || newStatus === 'SlotTransfer');

    // Configurar el modal
    this.statusConfirmationConfig = {
      title: 'Confirmar cambio de estado',
      message: this.showReasonInput
        ? `¿Está seguro de cambiar el estado del acuerdo corporativo a "${statusLabel}"?\n\nEste cambio requiere una justificación.`
        : `¿Está seguro de cambiar el estado del acuerdo corporativo a "${statusLabel}"?`,
      type: 'warning',
      confirmText: 'Sí, cambiar estado',
      cancelText: 'Cancelar',
      showIcon: true,
      icon: 'fa-exchange-alt'
    };

    // Mostrar el modal
    this.showStatusConfirmationModal = true;
  }

  /**
   * Ejecuta el cambio de estado después de la confirmación del modal
   */
  onStatusConfirmationConfirmed(): void {
    if (!this.pendingStatusChange || !this.details) {
      this.onStatusConfirmationCancelled();
      return;
    }

    // Validar razón si es requerida
    if (this.showReasonInput && !this.statusChangeReason.trim()) {
      this.toast.showToast('warning', 'Debes ingresar una razón para este cambio de estado.');
      return; // No cerrar el modal, permitir al usuario ingresar la razón
    }

    const { newStatus, statusLabel } = this.pendingStatusChange;
    const reason = this.showReasonInput ? this.statusChangeReason.trim() : '';

    // Cerrar el modal
    this.showStatusConfirmationModal = false;

    // Ejecutar el cambio
    this.loading.show();
    this.enrollmentService.updateCorporateStatus(this.agreementId, this.getStatusValueFromString(newStatus), reason).subscribe({
      next: (response) => {
        this.toast.showToast('success', `Estado del acuerdo corporativo actualizado a "${statusLabel}" exitosamente.`);
        this.loadDetails(); // Recargar datos
        this.loading.hide();
        this.pendingStatusChange = null;
        this.statusChangeReason = '';
      },
      error: (err) => {
        console.error('Error al actualizar estado:', err);
        this.toast.showToast('error', err.message || 'Error al actualizar el estado del acuerdo corporativo.');
        // Restaurar el valor anterior en el dropdown en caso de error
        this.selectedEnrollmentStatus = this.enrollmentStatus;
        this.loading.hide();
        this.pendingStatusChange = null;
        this.statusChangeReason = '';
      }
    });
  }

  /**
   * Cancela el cambio de estado
   */
  onStatusConfirmationCancelled(): void {
    this.showStatusConfirmationModal = false;

    // Restaurar el valor anterior en el dropdown
    this.selectedEnrollmentStatus = this.enrollmentStatus;

    this.pendingStatusChange = null;
    this.statusChangeReason = '';
  }

  /**
   * Convierte un string de estado a su valor numérico
   */
  private getStatusValueFromString(status: string): number {
    const statusMap: { [key: string]: number } = {
      'Pending': 0,
      'Active': 1,
      'Withdrawn': 2,
      'Deferred': 3,
      'Completed': 4,
      'Refinanced': 5,
      'Reversed': 6,
      'Refund': 7,
      'SlotTransfer': 8
    };
    return statusMap[status] ?? 1; // Default a Active
  }

  /**
   * Inicializa el valor seleccionado del dropdown de estado
   */
  private initSelectedEnrollmentStatus(): void {
    this.selectedEnrollmentStatus = this.enrollmentStatus;
  }

  /**
   * Verifica si el usuario tiene permiso para actualizar el estado de la inscripción corporativa
   */
  get canUpdateEnrollmentStatus(): boolean {
    return this.authorizationService.hasPermission('ENROLLMENTS_UPDATE');
  }

  // ============== MÉTODOS PARA ANULACIÓN ==============

  /**
   * 🆕 Abre el modal de anulación
   */
  openCancellationModal(): void {
    this.cancellationReason = '';
    this.showCancellationModal = true;
  }

  /**
   * 🆕 Cierra el modal de anulación
   */
  closeCancellationModal(): void {
    this.showCancellationModal = false;
    this.cancellationReason = '';
  }

  /**
   * 🆕 Ejecuta la anulación corporativa
   */
  confirmCancellation(): void {
    if (!this.cancellationReason || this.cancellationReason.trim().length === 0) {
      this.toast.showToast('warning', 'El motivo de anulación es obligatorio.');
      return;
    }

    this.isCancelling = true;
    this.loading.show();

    this.corporateService.cancel(this.agreementId, this.cancellationReason).subscribe({
      next: (response) => {
        this.loading.hide();
        this.isCancelling = false;
        this.closeCancellationModal();
        this.toast.showSuccess('Inscripción corporativa anulada exitosamente.');
        this.loadDetails(); // Recargar detalles
        this.loadMovementHistory(); // Recargar historial
      },
      error: (err) => {
        this.loading.hide();
        this.isCancelling = false;
        console.error('Error al anular inscripción corporativa:', err);
        this.toast.showError(err.message || 'Error al anular la inscripción corporativa.');
      }
    });
  }

}


